/**
 * @unscoped — ProjectPage(프로젝트 메뉴) 의 i18n — project-menu-detailing (2026-06-10).
 *
 * task-i18n / calendar-i18n 패턴 일관 (lib/<domain>-i18n.ts graduation).
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko) 명시
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

/* ════════════════════════════════════════════════════════════════════════════
 * ★★★ `ProjectI18nKey` 에 ⇒ ⭐ «눈»을 준다 — 2026-10-10
 *
 * ## ⭐ 무엇을 바꿨나 ⇒ ★ 두 줄
 *
 *   바로 아래 ⇒ 선언에 붙어 있던 ★ 명시 타입 주석을 ⇒ ⭐ 뗐다
 *   닫는 자리 ⇒ 같은 꼴을 ★ `satisfies` 로 ⇒ ⭐ 다시 걸었다
 *
 * ⇒ ★ 까닭 ⇒ ⭐ 선언에 `Record<string, …>` 가 붙어 있으면 ★ `keyof` 가 ⇒ ⭐ **`string`** 이 된다.
 *   ⇒ ⇒ ★ 즉 `ProjectI18nKey` ＝ `string` 이었고 ⇒ ⇒ ⛔ `tProject(아무글자)` 가 ★ 통과했다.
 *
 * ## ⛔⛔ ★ 왜 이 판이 섰나 — ⇒ ⭐ «운영에 나간» 결함이 ★ 있었다
 *
 *   ⛔ `project.team.aw.add_aria` ⇒ ★ 선언이 ⭐ «없는데» ⇒ ★ 세 자리에서 불렸다:
 *      `pages/ProjectPage/TeamInvitePickerModal.tsx:290 · 360 · 429` 의 ★ `aria-label`
 *   ⇒ ★ `tProject` 는 ⇒ ⭐ 모르는 열쇠면 ★ **그 열쇠 글자를 그대로 돌려준다**(아래 함수 참조)
 *     ⇒ ⇒ ⛔ 그래서 ★ 화면 낭독기가 ⇒ ⭐ 그 열쇠 이름을 ★ 소리내어 읽었다.
 *   ⇒ ⇒ ⭐ 그 열쇠는 2026-10-10 정본 이관(PR #2396)이 ★ 메웠다. ⇒ 이 판은 ★ 그 «눈»을 세운다.
 *
 * ## ⚠ 「0건인데 왜 고치나」 ⇒ ⭐ 0 이 «안 쟀다»가 아님을 ★ 재서 보였다
 *
 *   잰 것 ⇒ `npx tsc -p apps/frontend/tsconfig.app.json --noEmit` · ★ 잰 파일 ⭐ 1,156
 *   ⇒ ⛔ `tsconfig.json` 으로 재지 말 것 — 그 판은 ★ 0개를 재고도 ⇒ ⭐ 같은 「EXIT=0 · 0줄」을 찍는다.
 *
 *                        ① 성한 소스   ② 없는 열쇠를 부름   ③ 언어 한 칸이 빠진 선언
 *     Ⓐ 뗴기 전            0 ✅         ⛔ ⭐ 0건            ✅ TS2741 @19 (선언 자리)
 *     Ⓑ 주석만 뗌          0 ✅         ✅ TS2345            ⚠ TS7053×2 ＋ TS7006×2 @1107
 *     Ⓒ satisfies (이 판)  0 ✅         ✅ TS2345            ✅ TS2741 @19 (선언 자리)
 *
 *   ⇒ ★ Ⓐ② 의 ⭐ 0 이 ⇒ ★ 「눈가림」의 ⭐ 증거다 — ⇒ 없는 열쇠를 심어도 ⛔ 조용했다.
 *   ⇒ ⭐⭐ 그리고 ★ Ⓑ③ 때문에 ⇒ ⭐ «그냥 떼지» 않았다. ★ 그 주석은 ⇒ ⭐ 「언어 한 칸이 빠진
 *     선언」을 ★ 막아 주고 있었고, ⇒ 떼기만 하면 ⇒ ⛔ 그 오류가 ★ 선언 자리를 떠나 ⇒ ⭐ 조회
 *     자리의 ★ «암묵적 any» 로 바뀐다 ⇒ ⇒ ⚠ 「무엇이 빠졌다」고 ★ 말해 주지 않는다.
 *     ⇒ ✅ `satisfies` 는 ★ 둘을 ⭐ 다 준다 — ⇒ 눈 ＋ ★ 제자리의 TS2741.
 *
 * ## ⛔⛔ 그래서 ★ 무엇이 ⭐ «안» 잠겼나 — ⇒ ★ 752 / 765 다 (⛔ 「전부」가 아니다)
 *
 *   부르는 자리 ⭐ 765 (제품 42파일 · 시험 제외 · 괄호 맞춰 뽑음 · `getProject(` 오검출 1 뺌)
 *     리터럴 732 ✅ · 삼항 18 ✅ · greetingKey()/DUE_FILTER_LABEL 2 ✅ · 정의 자신 1
 *     ⇒ ⛔ ★ 남는 ⭐ 12 자리 ⇒ ⇒ ★ 호출자가 ⭐ «제 손으로 붙인 단언» 때문이다:
 *
 *       pages/ProjectPage/MyTasksDetailPage.tsx:158    status 템플릿 ＋ 단언
 *       pages/ProjectPage/MyTasksDetailPage.tsx:194    statusKey   ＋ 단언   (쓰는 곳 :216)
 *       pages/ProjectPage/MyTasksDetailPage.tsx:195    priorityKey ＋ 단언   (쓰는 곳 :220)
 *       pages/ProjectPage/MyTaskProgressList.tsx:71    statusKey   ＋ 단언   (쓰는 곳 :83)
 *       pages/ProjectPage/MyTaskProgressList.tsx:72    priorityKey ＋ 단언   (쓰는 곳 :91)
 *       pages/ProjectPage/ProjectSearchPage.tsx:229    status 템플릿 ＋ 단언
 *       pages/ProjectPage/StatsDetailPage.tsx:229      status 템플릿 ＋ 단언
 *       pages/ProjectPage/TeamSetupWizardModal.tsx:171 wiz.step 템플릿 ＋ 단언
 *       pages/ProjectV2/MyTaskCreateModal.tsx:177      mytask.pri_ 템플릿 ＋ 단언
 *       pages/ProjectV2/MyTasksBoard.tsx:604           mytask.pri_ 템플릿 ＋ 단언
 *       pages/ProjectV2/MyTasksBoard.tsx:615           status 템플릿 ＋ 단언
 *       pages/ProjectV2/SpaceV2Page.tsx:359            ROLE_LABEL 표 ＋ 단언
 *
 *   ⇒ ★ 그 12 자리의 열쇠 ⭐ 20개는 ⇒ ★ 손으로 풀어 ⭐ 선언에 대고 셌다 ⇒ ⇒ ✅ 없는 것 ★ 0
 *     (status 4 · wiz.step 3 · mytask.pri_ 3 · priority 3 · space.role_ 4 · mytasks.filter_ 3)
 *   ⇒ ⚠ 그러나 ★ 그것은 ⭐ 「2026-10-10 에 쟀다」일 뿐 ⇒ ⇒ ⛔ 앞으로를 ★ 막아 주지 «않는다».
 *
 * ⏳ ★ 이 절의 ⭐ 만료 조건 ⇒ ⇒ ★ 위 단언들을 ⭐ 걷고 ★ 표(`Record<…>`)로 바꾼 날.
 *   ⇒ ★ 본은 ⭐ 이미 이 레포에 있다 — `MyTasksBoard.tsx:73` 의 `DUE_FILTER_LABEL`(값 타입이
 *     리터럴 유니온) · `ProjectHomeSpaces.tsx:111` 의 `greetingKey()`(리터럴 유니온 반환).
 *     ⇒ ⇒ ★ 그 둘은 ⭐ 사보타주로 ★ 「잡힌다」를 확인했다(TS2820 ＋ TS2345 · TS2345).
 *   ⇒ ⛔ 그 일을 ★ 이 판에 ⭐ 넣지 않았다 — ⇒ 프로젝트 메뉴 이관이 ★ 같은 파일들을 덮는 중이다.
 *
 * ⚠ 이 머리말은 ★ 제 가드를 ⭐ 먹인 적이 있다 — ⇒ 그 글자를 ★ 인용하면 ⭐ 셈이 부풀어
 *   ⇒ 「안 바뀌었다」로 읽힌다. ⇒ ⇒ ✅ 그래서 ★ 여기서는 ⭐ 그 글자를 ★ «적지 않고» 말로 썼고,
 *   ⇒ 재는 쪽은 ★ 주석을 ⭐ 길이 보존으로 덮고 센다.
 * ════════════════════════════════════════════════════════════════════════════ */
/*
 * ★★ 2026-10-12 사용자 결정 ⓐ — ko «만» 「팀」을 「스페이스」로 통일했다.
 *
 * ```
 * 사용자 원문 ⇒ 「2.낱말통일-내 스페이스  3.통계카드,위젯이 쓰는 내팀 도 수정해줘」
 *   그 앞 ⇒ 「1.아니요 프로젝트는 무조건 스페이스 안에 있어야합니다. 2.네 통일해야합니다.」
 * 바꾼 수 ⇒ ko 48 자리 (ⓐ 46 ＋ ⓑ project.stats.teams 1 ＋ ⓓ location_personal 1)
 * 남긴 수 ⇒ ko 9 자리 ⇒ «보통 낱말»로 쓰인 자리다(onb·wiz·tmpl.scope_shared)
 *   ⇒ 「스페이스 협업 시작하기」가 한국어로 어색해질 수 있어 사용자가 정할 자리다
 *   ⇒ 관제가 그 목록을 사용자에게 올렸다 ⇒ 답이 오면 그때 바꾼다
 * ```
 *
 * ## ⛔ en·ja 는 «일부러» Team / チーム 로 남겼다 — 고치지 말 것
 *
 * ```
 * 관제가 셋을 물었다 ⇒ ⓐ ko 만 / ⓑ 셋 다 / ⓒ ko 먼저 en·ja 는 다음 차
 * 사용자 답 ⇒ ⓐ ⇒ ⇒ 즉 한 열쇠가 「스페이스 / Team / チーム」로 «두 말을 하는» 것을
 *   사용자가 «알고» 고른 것이다.
 *
 * ⚠ 그 수에는 «기준»을 붙인다 — 기준이 없으면 다음 사람이 다르게 세어 어긋난다
 *   (2026-10-12 에 관제와 내가 실제로 어긋났다 ⇒ 58 ⇔ 17):
 *     en ⇒ `en:` 값에 team|Team 이 든 줄 ⇒ 58   (관제 실측 · 이것이 「손댈 줄」의 수다)
 *     ja ⇒ `ja:` 값에 チーム 이 든 줄     ⇒ 54   (관제 실측)
 *   ⛔ 「줄에 대문자 Team」으로 세면 17 이 나온다 — 영어는 대부분 «소문자»다
 *     ('New team' · 'My teams' · 'No teams yet' · 'Failed to load teams')
 *   ⛔ 「줄에 team 아무거나」로 세면 109 다 — 열쇠 이름의 _team 까지 세어 뜻이 없다
 * ⛔ 그러니 이것을 「불일치」로 읽고 고치지 말 것 — 결정이다.
 * ⏳ 바꿀지는 사용자가 뒤에 정한다. 물음은 이미 올렸고 답이 ⓐ 였다.
 * ⚠ 그리고 이 불변식을 «가드로 두지 않았다» — 「en 에 Team 이 58 줄 있어야 한다」는
 *   사용자가 뒤에 ⓑ 로 바꿀 때 «막는 쪽»이 된다(관제 지시). 주석으로 족하다.
 * ```
 */
const MESSAGES = {
  // ===== 페이지 헤더 =====
  'project.title': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'project.btn.new_team': { ko: '새 스페이스', en: 'New space', ja: '新規スペース' },
  'project.btn.new_project': { ko: '새 프로젝트', en: 'New project', ja: '新規プロジェクト' },
  // ⭐ 2026-10-07 — ko 가 「프로젝트 «대시보드» 조회 실패」였다. 그 글자는 2026-07-25 이후
  //   아무 화면의 이름도 아니므로 ⇒ 「프로젝트 홈」으로 고쳤다(사용자 결정 ⓑ 의 뜻을 그대로).
  // ⚠ 다만 ★ 이것은 «죽은 열쇠»다 — 부르는 곳이 0건이다(2026-10-07 실측:
  //   git grep -n "project.err_fetch" -- apps/frontend/src ⇒ 이 선언 한 줄뿐 · 동적 호출도 0).
  //   ⇒ ⛔ 그러니 「사용자가 이 글을 본다」는 ★ 일어나지 않는다 — 고친 값은 ⭐ «되살릴 때»를 위한 것이다.
  //   ⇒ 같은 꼴을 calendar-i18n.ts:602 가 이미 적어 두었다(그쪽 err_fetch 도 죽은 키다).
  //   ⇒ ❓ 지울지는 따로 정할 일이다 — 지우는 것은 이 결정의 범위가 아니다.
  'project.err_fetch': { ko: '프로젝트 홈 조회 실패', en: 'Failed to load project home', ja: 'プロジェクトホーム取得失敗' },

  // ===== 통계 카드 =====
  // ⛔⛔ 2026-10-12 — project.stats.* 열쇠 «여덟»을 걷었다(통계 카드·주간 완료율 위젯과 함께).
  //   ⇒ 그 부품들이 사라져 쓰는 곳이 0 이 됐다 ⇒ 고아 열쇠를 남기지 않는다.
  //   ⇒ 남은 둘 ⇒ my_tasks(2차 메뉴 라벨) · teams(TeamsWidget) — ★ 이름이 project.stats.* 이지만
  //     «통계 화면의 것이 아니다». project-stats-removed.guard.test.ts 가 「고아 0」을 잠근다.
  'project.stats.teams': { ko: '내 스페이스', en: 'My spaces', ja: 'マイスペース' },
  'project.stats.my_tasks': { ko: '내 할일', en: 'My tasks', ja: 'マイタスク' },

  // ===== 내가 하는 일 (진행 목록) =====
  'project.mytasks.heading': { ko: '내가 하는 일', en: 'My work', ja: '担当タスク' },
  'project.mytasks.empty': { ko: '진행 중인 할일이 없습니다.', en: 'No tasks in progress.', ja: '進行中のタスクはありません。' },
  'project.mytasks.err_fetch': { ko: '내 할일 조회 실패', en: 'Failed to load my tasks', ja: 'マイタスク取得失敗' },
  'project.mytasks.due': { ko: '~{date}', en: 'due {date}', ja: '~{date}' },
  'project.mytasks.open_aria': { ko: '{title} — 프로젝트 {project} 로 이동', en: '{title} — open project {project}', ja: '{title} — プロジェクト {project} を開く' },

  // ===== 상태·우선순위 라벨 =====
  'project.status.TODO': { ko: '할 일', en: 'To do', ja: '未着手' },
  'project.status.IN_PROGRESS': { ko: '진행 중', en: 'In progress', ja: '進行中' },
  'project.status.REVIEW': { ko: '검토', en: 'Review', ja: 'レビュー' },
  'project.status.DONE': { ko: '완료', en: 'Done', ja: '完了' },
  'project.priority.HIGH': { ko: '높음', en: 'High', ja: '高' },
  'project.priority.MEDIUM': { ko: '보통', en: 'Medium', ja: '中' },
  'project.priority.LOW': { ko: '낮음', en: 'Low', ja: '低' },

  // ===== 팀 패널 =====
  'project.team.heading': { ko: '스페이스', en: 'Spaces', ja: 'スペース' },
  'project.team.personal': { ko: '내 개인 프로젝트', en: 'My personal projects', ja: '個人プロジェクト' },
  'project.team.members': { ko: '스페이스 멤버', en: 'Space members', ja: 'スペースメンバー' },
  'project.team.invite': { ko: '초대', en: 'Invite', ja: '招待' },
  'project.team.empty': { ko: '스페이스가 없습니다. 새 스페이스를 만들어 보세요.', en: 'No spaces yet. Create one.', ja: 'スペースがありません。新規作成してください。' },
  'project.team.err_fetch': { ko: '스페이스 조회 실패', en: 'Failed to load spaces', ja: 'スペース取得失敗' },
  'project.team.role.LEADER': { ko: '리더', en: 'Leader', ja: 'リーダー' },
  'project.team.role.MEMBER': { ko: '멤버', en: 'Member', ja: 'メンバー' },
  'project.team.member_count': { ko: '멤버 {n}', en: '{n} members', ja: 'メンバー {n}' },
  'project.team.select_aria': { ko: '스페이스 선택', en: 'Select space', ja: 'スペース選択' },

  // ===== 팀 생성 모달 =====
  'project.team.create.title': { ko: '새 스페이스 만들기', en: 'Create space', ja: 'スペース作成' },
  'project.team.create.label_name': { ko: '스페이스 이름', en: 'Space name', ja: 'スペース名' },
  'project.team.create.placeholder_name': { ko: '예: 알파 스쿼드', en: 'e.g. Alpha squad', ja: '例: アルファスペース' },
  'project.team.create.label_desc': { ko: '설명 (선택)', en: 'Description (optional)', ja: '説明（任意）' },
  'project.team.create.err_name': { ko: '스페이스 이름을 입력하세요.', en: 'Please enter a space name.', ja: 'スペース名を入力してください。' },
  'project.team.create.err_save': { ko: '스페이스 생성 실패', en: 'Failed to create space', ja: 'スペース作成失敗' },

  // ===== 팀원 초대 모달 =====
  'project.invite.title': { ko: '스페이스원 초대', en: 'Invite members', ja: 'メンバー招待' },
  'project.invite.placeholder_search': { ko: '이름 또는 이메일 검색', en: 'Search by name or email', ja: '名前またはメールで検索' },
  'project.invite.empty': { ko: '초대 가능한 사용자가 없습니다.', en: 'No users to invite.', ja: '招待可能なユーザーがいません。' },
  'project.invite.add': { ko: '추가', en: 'Add', ja: '追加' },
  'project.invite.added': { ko: '추가됨', en: 'Added', ja: '追加済み' },
  'project.invite.note': {
    ko: '초대하면 즉시 스페이스에 추가됩니다. 기존 프로젝트 멤버십에는 영향이 없습니다 (프로젝트별 멤버는 별도 관리).',
    en: 'Invited users are added immediately. Existing project memberships are not affected (project members are managed separately).',
    ja: '招待すると即時追加されます。既存プロジェクトのメンバーには影響しません（プロジェクト別に管理）。',
  },
  'project.invite.err': { ko: '초대 실패', en: 'Invite failed', ja: '招待失敗' },
  'project.invite.err_search': { ko: '검색 실패', en: 'Search failed', ja: '検索失敗' },

  // ===== 팀 프로젝트 목록 =====
  'project.list.heading': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'project.list.empty': { ko: '프로젝트가 없습니다. 새 프로젝트를 추가하세요.', en: 'No projects. Add one.', ja: 'プロジェクトがありません。追加してください。' },
  'project.list.add': { ko: '프로젝트 추가', en: 'Add project', ja: 'プロジェクト追加' },
  'project.list.progress_aria': { ko: '진행률 {percent}%', en: 'Progress {percent}%', ja: '進捗 {percent}%' },
  'project.list.open_aria': { ko: '{name} 태스크 보드 열기', en: 'Open {name} task board', ja: '{name} タスクボードを開く' },
  'project.list.task_count': { ko: '태스크 {n}', en: '{n} tasks', ja: 'タスク {n}' },

  // ===== 프로젝트 생성 모달 =====
  'project.create.title': { ko: '새 프로젝트', en: 'New project', ja: '新規プロジェクト' },
  'project.create.label_name': { ko: '프로젝트 이름', en: 'Project name', ja: 'プロジェクト名' },
  'project.create.label_desc': { ko: '설명 (선택)', en: 'Description (optional)', ja: '説明（任意）' },
  'project.create.label_due': { ko: '마감일 (선택)', en: 'Due date (optional)', ja: '期限（任意）' },
  'project.create.label_team': { ko: '소속 스페이스', en: 'Space', ja: '所属スペース' },
  'project.create.team_none': { ko: '무소속 (개인)', en: 'No space (personal)', ja: '無所属（個人）' },
  'project.create.note': {
    ko: '스페이스를 선택하면 현재 스페이스 멤버가 프로젝트 멤버로 함께 등재됩니다 (리더→매니저, 멤버→멤버).',
    en: 'Selecting a space enrolls current space members into the project (leader→manager, member→member).',
    ja: 'スペースを選択すると現メンバーがプロジェクトに登録されます（リーダー→マネージャー、メンバー→メンバー）。',
  },
  'project.create.err_name': { ko: '프로젝트 이름을 입력하세요.', en: 'Please enter a project name.', ja: 'プロジェクト名を入力してください。' },
  'project.create.err_save': { ko: '프로젝트 생성 실패', en: 'Failed to create project', ja: 'プロジェクト作成失敗' },

  // ===== 프로젝트 설정 모달 (2026-08-05) — 생성자 본인만 진입 =====
  'project.settings.open': { ko: '프로젝트 설정', en: 'Project settings', ja: 'プロジェクト設定' },
  'project.settings.title': { ko: '프로젝트 설정', en: 'Project settings', ja: 'プロジェクト設定' },
  'project.settings.label_delegate': { ko: '소속 스페이스 (위임)', en: 'Space (delegate)', ja: '所属スペース（委任）' },
  'project.settings.delegate_note': {
    ko: '다른 스페이스으로 위임하면 그 스페이스의 멤버가 프로젝트 멤버로 추가 등재됩니다. 기존 멤버는 그대로 유지됩니다.',
    en: 'Delegating adds the target space’s members to the project. Existing members are kept.',
    ja: '他スペースへ委任すると、そのスペースのメンバーがプロジェクトに追加されます。既存メンバーは維持されます。',
  },
  'project.settings.btn_save': { ko: '변경 저장', en: 'Save changes', ja: '変更を保存' },
  'project.settings.btn_delete': { ko: '프로젝트 삭제', en: 'Delete project', ja: 'プロジェクトを削除' },
  'project.settings.err_save': { ko: '프로젝트 수정 실패', en: 'Failed to update project', ja: 'プロジェクト更新失敗' },
  'project.settings.err_team_not_found': {
    ko: '위임할 스페이스를 찾을 수 없거나 그 스페이스의 멤버가 아닙니다.',
    en: 'Target space not found, or you are not a member of it.',
    ja: '委任先スペースが見つからないか、そのスペースのメンバーではありません。',
  },

  // ===== v2 드릴다운 — 상세 화면 공통 (2026-06-10) =====
  // ⛔ 2026-10-07 사용자 결정 ⓑ — 'project.detail.back' / '_aria'(「대시보드」·「프로젝트 대시보드로
  //   돌아가기」)를 ★ 지웠다. 쓰는 곳이 DetailPageHeader «하나»였고, 그 자리는 이제
  //   'project.home.back_home' / '_aria' 를 쓴다.
  //   ⇒ ⚠ 값만 바꾸면 두 열쇠가 «같은 값»이 되어 다음 사람이 한쪽만 고친다 — 그래서 합쳤다.
  //   ⇒ ⛔ 「대시보드」를 되살리지 마십시오. 그 글자는 2026-07-25 이후 아무 화면의 이름도 아니다.
  'project.detail.teams.title': { ko: '스페이스', en: 'Spaces', ja: 'スペース' },
  // 2026-07-25 — 3c 프로젝트 홈(좌측 2차 네비 + 위젯 4종).
  'project.home.nav_collapse': { ko: '네비 접기', en: 'Collapse nav', ja: 'ナビを折りたたむ' },
  'project.home.all_teams': { ko: '스페이스 전체 보기', en: 'View all spaces', ja: 'スペース一覧' },
  'project.home.my_projects': { ko: '내 프로젝트', en: 'My projects', ja: 'マイプロジェクト' },
  // 2026-08-18 — 좌측 2차 네비 라벨 개편. '내 팀'(project.stats.teams)은 통계 카드·위젯이
  // 계속 쓰므로 건드리지 않고, 네비 전용 키를 따로 둔다(같은 문구를 두 뜻으로 쓰지 않기).
  // '(가제)'는 확정 명칭이 정해질 때까지의 임시 표기 — 확정되면 이 값만 바꾸면 된다.
  // 2026-10-07 — 2차 메뉴 맨 위의 「홈」(→ /project). 운영 확인에서 올라온 요청의 글자 그대로다.
  // ⚠ 「프로젝트 홈」으로 안 쓴다 — 이 메뉴의 머리가 이미 'project.title' 「프로젝트」라서
  //   그 아래 「프로젝트 홈」은 「프로젝트 > 프로젝트 홈」으로 읽힌다.
  // ⭐ 2026-10-07 사용자 결정 ⓑ — 같은 곳(/project)으로 가는 글자는 ★ 「프로젝트 홈」 하나로 모았다.
  //   ⇒ ⚠ 그래도 «이 열쇠»는 ★ 「홈」 그대로다 — ⭐ 그것이 «의도»다:
  //      · 상세 머리말·측면 패널 ⇒ 「프로젝트 홈」 (그 화면에는 「프로젝트」라는 머리가 ★ 없다)
  //      · 2차 메뉴 «안»        ⇒ 「홈」         (이 메뉴 머리가 ★ 'project.title' 「프로젝트」다)
  //   ⇒ ⛔ 「둘이니 하나로 줄이자」로 되돌리지 마십시오 — 그것은 이 결정이 «아니다».
  //      (project-home-link-words.test.ts 가 그 둘을 ★ 양쪽으로 잠근다.)
  /*
   * ⭐⭐ 2026-10-08 사용자 결정 ⓐ — ★ 명칭 «확정»: 「작업공간(가제)」 ⇒ ⭐ 「스페이스」.
   *
   * ⚠ 이것은 ★ 깃발 뒤가 ⛔ «아니다» — ⭐ 머지되면 ★ 운영 `/project` 홈의 글자가 ⭐ 바뀐다.
   *   ⇒ 그래서 ★ 사용자 결정을 ⭐ 받고 고쳤다(⛔ 내가 고른 말이 아니다).
   *
   * ⭐ 정본(`feature/dev-syk`)이 ★ 이 일을 ⭐ 이미 해 두었고(2026-08-21) ⇒ ★ 그 글을 따른다:
   * ```
   * 「임시 표기를 ★ «예고대로» ⇒ ⭐ «이 값만» 바꿔 걷어냈다
   *   (★ 열쇠 이름 `nav_workspace` 는 ⭐ «그대로» — ★ 참조처가 여럿이고 ⭐ 뜻은 안 달라진다)
   *   컨텐츠 쪽 '내 스페이스'(aw001.spaces_heading)와 ★ 같은 말을 쓰게 돼 ⭐ 좌우가 맞는다」
   * ```
   * ⇒ ⛔ 그러므로 ★ 열쇠 이름을 ⭐ 바꾸지 «마십시오» — ★ 값만 바꾸는 것이 ⭐ 이 판의 전부다.
   *   ⇒ ⚠ 가드가 그것을 잰다(`project-space-rename.guard.test.ts` — 「열쇠 이름은 그대로다」).
   * ⇒ ⚠ 그리고 ★ 「(가제)」가 사라진 것은 ⭐ 「임시가 끝났다」는 뜻이다 —
   *   ⇒ ⛔ 「내 팀」(`project.stats.teams`)은 ★ 통계 카드·위젯이 계속 쓰므로 ⭐ 안 건드린다
   *     (위 2026-08-18 주석의 그 까닭 그대로 — ★ 같은 문구를 두 뜻으로 쓰지 않는다).
   */
  'project.home.nav_workspace': { ko: '스페이스', en: 'Spaces', ja: 'スペース' },
  'project.home.nav_favorites': { ko: '즐겨찾기', en: 'Favorites', ja: 'お気に入り' },

  /*
   * ⭐ 즐겨찾기 묶음·해제 (정본 2026-09-04·08-21 · 우리 이식 2026-10-10).
   *
   * ⚠ 'fav_group_*' 는 ★ 묶음 머리글이라 ⇒ ⭐ «짧아야» 한다(머리글 폭이 10.5px 글자다).
   *   ⇒ ⚠ 'fav_group_space' 의 ko 가 ★ 「스페이스」인 것은 ⇒ ⭐ 2026-10-08 사용자 결정 ⓐ 와 ★ 같은 말이다
   *     ⇒ ⇒ ⛔ 둘은 ★ «다른 열쇠»다(nav_workspace ⇔ fav_group_space) — ⇒ ⭐ 자리가 다르다.
   *        ⇒ ⇒ ★ 한쪽을 고칠 때 ⭐ 다른 쪽도 봐야 한다.
   * ⚠ 'favorites_remove' 는 ★ aria-label 겸 title 이라 ⇒ ⭐ 이름이 들어간다 —
   *   ⇒ ⛔ 「해제」만 쓰면 ★ 목록의 ✕ 전부가 ⭐ 같은 이름이 된다(읽어 주는 쪽이 못 가른다).
   * ⚠ 'favorites_empty_hint' 의 ☆ 는 ★ FavoriteStar 가 ⭐ 실제로 그리는 글자다
   *   ⇒ ⇒ ★ 이모지가 ⛔ 아니다(U+2606 기하 도형) ⇒ ⇒ ⭐ 이모지 가드 범위 밖이고 ★ 그 파일이 이미 쓴다.
   */
  'project.home.favorites_empty_hint': {
    ko: '스페이스 카드의 ☆ 를 눌러 담아 보세요.',
    en: 'Tap ☆ on a space card to add one.',
    ja: 'スペースカードの ☆ を押して追加できます。',
  },
  'project.home.fav_group_space': { ko: '스페이스', en: 'Spaces', ja: 'スペース' },
  'project.home.fav_group_project': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'project.home.fav_group_menu': { ko: '메뉴', en: 'Menus', ja: 'メニュー' },

  /*
   * ⭐ 팀 멤버 초대 고르개 (정본 2026-08-19 · 우리 이식 2026-10-10 · 판 F-1).
   *
   * ⇒ ⭐ 「보드를 보다 ★ 사람 하나 넣으려던 흐름」을 ⇒ ★ 같은 자리에서 끝내는 모달의 글자다.
   * ⚠ 'project.team.aw.*' 는 ★ 정본의 ⭐ 이름 그대로 뒀다 — ⇒ ⛔ 'aw' 는 ★ AW-001 설계서의
   *   ⇒ ⇒ 머리글자다(그 화면 묶음). ⇒ ⭐ 이름을 고치면 ★ 정본과 ⭐ 맞대 볼 수 없게 된다.
   * ⚠ 'project.invite.pick.*' 는 ★ 이 모달 전용이다.
   */
  'project.invite.pick.all_in': {
    ko: '이 스페이스 사람은 모두 이미 들어와 있습니다.',
    en: 'Everyone here is already a member.',
    ja: 'ここの全員がすでにメンバーです。',
  },
  'project.invite.pick.err_n': {
    ko: '{names} 초대에 실패했습니다. 다시 시도해 주세요.',
    en: 'Failed to invite {names}. Please try again.',
    ja: '{names} の招待に失敗しました。もう一度お試しください。',
  },
  'project.invite.pick.label': { ko: '멤버 초대', en: 'Invite members', ja: 'メンバー招待' },
  'project.invite.pick.submit': { ko: '초대하기', en: 'Invite', ja: '招待する' },
  'project.invite.pick.submit_n': { ko: '초대하기 ({n}명)', en: 'Invite {n}', ja: '招待する（{n}名）' },
  'project.invite.pick.subtitle': {
    ko: '추가할 멤버를 고르세요. 초대하면 즉시 스페이스에 들어옵니다.',
    en: 'Pick who to add. Invited people join the space immediately.',
    ja: '追加するメンバーを選んでください。招待すると即座にスペースに加わります。',
  },
  'project.invite.pick.subtitle_team': {
    ko: "'{team}' 에 추가할 멤버를 고르세요. 초대하면 즉시 스페이스에 들어옵니다.",
    en: "Pick who to add to '{team}'. Invited people join immediately.",
    ja: "'{team}' に追加するメンバーを選んでください。招待すると即座に加わります。",
  },
  'project.team.aw.count_people': { ko: '{n}명', en: '{n}', ja: '{n}名' },
  'project.team.aw.empty_org': { ko: '조직 정보가 없습니다.', en: 'No organization data.', ja: '組織情報がありません。' },
  'project.team.aw.more_people': { ko: '+ {n}명 더 보기', en: '+ {n} more', ja: '+ {n}名もっと見る' },
  'project.team.aw.none_selected': { ko: '선택된 멤버 없음 · 최근 함께 작업한 동료', en: 'No members selected · recent teammates', ja: '未選択 · 最近一緒に作業した同僚' },
  'project.team.aw.remove_aria': { ko: "'{name}' 선택 해제", en: "Unselect '{name}'", ja: "'{name}'の選択解除" },
  'project.team.aw.search_ph': { ko: '이름 또는 이메일 검색', en: 'Search by name or email', ja: '名前・メールで検索' },
  'project.team.aw.selected': { ko: '선택됨 {n}명', en: '{n} selected', ja: '選択 {n}名' },
  'project.team.aw.tab_dept': { ko: '부서별 찾기', en: 'Browse by department', ja: '部署から探す' },
  'project.team.aw.tab_recent': { ko: '최근 협업자', en: 'Recent collaborators', ja: '最近の協業者' },
  'project.home.favorites_remove': {
    ko: "'{name}' 즐겨찾기 해제",
    en: "Remove '{name}' from favorites",
    ja: "'{name}' をお気に入りから解除",
  },

  /*
   * ⭐ 휴지통 (정본 2026-09-07 · 우리 이식 2026-10-10) — ★ 지운 프로젝트를 되돌리는 자리.
   *
   * ⚠ 'trash_meta' 의 {date} 는 ★ formatDate 가 «그 사람 지역»으로 적은 글자다 —
   *   ⇒ 여기서 날짜 꼴을 정하지 않는다(ko/en/ja 에 같은 자리표시만 둔다).
   * ⚠ 'trash_restore' 는 ★ aria-label 겸 data-tip 이라 ⇒ 이름이 들어간다 —
   *   ⇒ ⛔ 「되돌리기」만 쓰면 목록의 단추 ★ 전부가 같은 이름이 된다(읽어 주는 쪽이 못 가른다).
   */
  'project.home.nav_trash': { ko: '휴지통', en: 'Trash', ja: 'ゴミ箱' },
  'project.home.trash_loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'project.home.trash_empty': { ko: '휴지통이 비어 있어요.', en: 'Trash is empty.', ja: 'ゴミ箱は空です。' },
  'project.home.trash_empty_hint': { ko: '지운 프로젝트가 여기로 들어와요.', en: 'Deleted projects land here.', ja: '削除したプロジェクトがここに入ります。' },
  'project.home.trash_err': { ko: '휴지통을 불러오지 못했습니다.', en: 'Could not load the trash.', ja: 'ゴミ箱を読み込めませんでした。' },
  'project.home.trash_restore': { ko: "'{name}' 되돌리기", en: "Restore '{name}'", ja: "'{name}' を元に戻す" },
  'project.home.trash_restore_err': { ko: '되돌리지 못했습니다.', en: 'Could not restore it.', ja: '元に戻せませんでした。' },
  'project.home.trash_meta': { ko: '{date} 삭제 · 태스크 {n}', en: 'Deleted {date} · {n} tasks', ja: '{date} 削除 · タスク {n}' },
  // ★ 완전 삭제 (2026-10-10 사용자 결정 · 단추는 2026-10-12) — ⇒ 글의 «까닭»은 서버가 들고 있다:
  //   apps/chat-server/src/modules/tasks/projects.routes.ts:134(이름 직접 입력) · :862(파일은 남는다)
  //   ⛔ 사용자에게 「고아가 쌓인다」·「걷는 자가 없다」를 말하지 않는다 — 그것은 우리 사정이다.
  'project.home.trash_purge': { ko: "'{name}' 완전 삭제", en: "Delete '{name}' permanently", ja: "'{name}' を完全に削除" },
  'project.home.purge_title': { ko: '완전 삭제', en: 'Delete permanently', ja: '完全に削除' },
  'project.home.purge_warn': { ko: "'{name}' 과 그 안의 태스크·댓글·체크리스트가 모두 지워집니다. 되돌릴 수 없습니다.", en: "'{name}' and all its tasks, comments and checklists will be deleted. This cannot be undone.", ja: "'{name}' とその中のタスク・コメント・チェックリストがすべて削除されます。元に戻せません。" },
  'project.home.purge_files_kept': { ko: '첨부한 파일은 지워지지 않습니다.', en: 'Attached files are not deleted.', ja: '添付したファイルは削除されません。' },
  'project.home.purge_confirm_label': { ko: '지우려면 프로젝트 이름을 직접 입력하십시오', en: 'Type the project name to confirm', ja: '削除するにはプロジェクト名を入力してください' },
  'project.home.purge_submit': { ko: '완전 삭제', en: 'Delete permanently', ja: '完全に削除' },
  'project.home.purge_cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'project.home.purge_err_name': { ko: '이름이 다릅니다. 다시 입력하십시오.', en: 'The name does not match. Please try again.', ja: '名前が違います。もう一度入力してください。' },
  'project.home.purge_err_forbidden': { ko: '완전 삭제는 프로젝트를 만든 사람만 할 수 있습니다.', en: 'Only the project creator can delete it permanently.', ja: 'プロジェクトを作成した人のみ完全に削除できます。' },
  'project.home.purge_err': { ko: '지우지 못했습니다.', en: 'Could not delete it.', ja: '削除できませんでした。' },
  'project.home.favorites_empty': { ko: '등록한 즐겨찾기가 없어요.', en: 'No favorites yet.', ja: 'お気に入りはまだありません。' },
  'project.home.all': { ko: '전체', en: 'All', ja: '全体' },
  // 주간 완료율 위젯 → /project/stats. 형제 둘의 「전체」와 뜻이 달라 열쇠를 따로 둔다
  // (저쪽은 «같은 것을 더» 보여 주고, 이쪽은 «다른 화면»으로 간다).
  // ⏳ 2026-10-12 — 쓰는 곳이 «0» 이 됐다(통계 화면을 걷으면서 그 단추가 사라졌다).
  //   ⛔ 지우지 않는다 — 열쇠 처분은 이 판의 범위가 아니다(관제 ⇒ 「열쇠는 남긴다」).
  //   ⇒ 만료 조건 ⇒ 「꺼진 갈래 전체」를 다루는 판에서 이 열쇠와 project.stats.* 를 함께 본다.
  'project.home.stats_detail': { ko: '상세', en: 'Details', ja: '詳細' },

  // ===== 통합 검색 결과 화면(/project/search) — 2026-10-07 사용자 결정 ㉠-2 =====
  // ⛔ 묶음 제목에 「내 」를 붙이지 않는다 — 검색 결과는 「내 것」이 아니다. 서버가 보는 범위는
  //   「내가 멤버인 스페이스 · 내가 볼 수 있는 프로젝트 · 그 프로젝트 안 태스크(남의 것도)」다.
  //   ⇒ 그래서 project.detail.mytasks.title 「내 할일」과 aw001.spaces_heading 「내 스페이스」를
  //     재사용하지 않았다(「같은 이름에 다른 뜻」을 또 만들지 않으려고).
  // ⇒ 「프로젝트」는 project.detail.projects.title · 빈 묶음은 project.detail.stats.empty_list 「해당 없음」.
  'project.search.title': { ko: '검색 결과', en: 'Search results', ja: '検索結果' },
  'project.search.submit': { ko: '찾기', en: 'Search', ja: '検索' },
  'project.search.spaces': { ko: '스페이스', en: 'Spaces', ja: 'スペース' },
  'project.search.tasks': { ko: '할일', en: 'Tasks', ja: 'タスク' },
  // ⭐ 2026-10-08 — 그 묶음이 상한(20)에서 잘렸을 때. ⛔ 「N건 더」라고 못 쓴다 —
  //   서버가 hasMore «하나»만 주고 total 은 안 준다(count 질의가 더 들기 때문).
  // ⚠ 그래서 ★ «수»를 말하지 않고 ⇒ ⭐ «할 수 있는 일»을 말한다. 눌러서 더 볼 길이 없으므로
  //   (그 끝점에 offset 이 없다) ⇒ ⛔ 단추로 만들지 않았다.
  'project.search.has_more': {
    ko: '더 있습니다 — 검색어를 좁혀 보세요.',
    en: 'More results — try a narrower search.',
    ja: 'さらにあります — 検索語を絞ってください。',
  },
  'project.home.count_teams': { ko: '{n}스페이스', en: '{n} spaces', ja: '{n}スペース' },
  'project.home.team_meta': { ko: '멤버 {m} · 프로젝트 {p}', en: '{m} members · {p} projects', ja: 'メンバー{m} · プロジェクト{p}' },
  'project.home.done_n': { ko: '완료 {n}', en: 'Done {n}', ja: '完了 {n}' },
  'project.home.open_n': { ko: '진행 {n}', en: 'Open {n}', ja: '進行 {n}' },
  // ⚠⚠ 이 자리는 ★ «건수»다 — 늦은 일이 몇 «건»인가(ProjectHomeWidgets 의 범례에서
  //   '완료 {n}' · '진행 {n}' 과 나란히 선다. 넘기는 값이 summary.myTasks.overdue 다).
  //   ⛔ 그런데 2026-10-02 ★ 사용자 결정(ⓨ)으로 ⭐ 라벨과 «같은 글자»를 쓴다.
  //   ⇒ ★ 그래서 화면은 ⭐ 「늦은 일 3건」을 「D+3」으로 적는다. ⛔ 그것을 «결함으로 읽지 말 것».
  //     (결정 전에 「그것은 3건이라는 뜻이고 완료 N·진행 N 과 나란한 셈 띠다」를 함께 올려 여쭈었고,
  //      그것을 알고 고르셨다. 되돌리려면 ★ 사용자에게 다시 물어야 한다.)
  //   ⇒ 형제 '완료 {n}' · '진행 {n}' 은 ★ 그대로다 — 범례가 「완료 5 · 진행 3 · D+2」로 읽힌다.
  'project.home.overdue_n': { ko: 'D+{n}', en: 'D+{n}', ja: 'D+{n}' },
  // ⛔ 2026-10-02 — 값이 '기한 초과' 였다. 같은 「기한」 칸을 다른 화면은 'D+{n}' 으로 그려
  //   사용자가 같은 뜻을 «두 가지»로 읽었다(사용자 결정으로 D+{n} 통일).
  //   ⚠ 열쇠 이름('overdue')과 값('D+{n}')이 어긋나 보인다 — 열쇠를 새로 만들거나 지우면
  //     부르는 줄까지 번져 더 위험하므로 «값만» 바꿨다. 지금 이 값을 가진 열쇠가 셋이고
  //     ProjectPage/overdue-label.test.ts 가 그 셋을 «함께» 잠근다(갈리면 빨개진다).
  //   ⚠ {n} 자리가 있으므로 ★ 부르는 곳이 n 을 «넘겨야» 한다(ProjectHomeWidgets dday()).
  'project.home.overdue': { ko: 'D+{n}', en: 'D+{n}', ja: 'D+{n}' },
  'project.home.back_home': { ko: '프로젝트 홈', en: 'Project home', ja: 'プロジェクトホーム' },
  'project.home.back_home_aria': { ko: '프로젝트 홈으로 돌아가기', en: 'Back to project home', ja: 'プロジェクトホームへ戻る' },
  // 2026-07-24 — 팀 화면 셸에서 프로젝트 화면으로 돌아가는 경로(design D-5).
  'project.team.back_projects': { ko: '내 프로젝트', en: 'My projects', ja: 'マイプロジェクト' },
  'project.team.back_projects_aria': { ko: '내 프로젝트 화면으로 돌아가기', en: 'Back to my projects', ja: 'マイプロジェクト画面へ戻る' },
  'project.team.open_in_projects': { ko: '이 스페이스 프로젝트 열기', en: 'Open in my projects', ja: 'このスペースのプロジェクトを開く' },
  'project.detail.projects.title': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'project.detail.mytasks.title': { ko: '내 할일', en: 'My tasks', ja: 'マイタスク' },
  'project.detail.stats.title': { ko: '통계', en: 'Stats', ja: '統計' },

  // ===== v2 — 팀 상세 =====
  'project.team.detail.select_hint': { ko: '왼쪽에서 스페이스를 선택하세요.', en: 'Select a space on the left.', ja: '左側でスペースを選択してください。' },
  'project.team.detail.projects_link': { ko: '스페이스의 프로젝트 보기 ({n})', en: 'View space projects ({n})', ja: 'スペースのプロジェクトを見る ({n})' },
  'project.team.edit': { ko: '수정', en: 'Edit', ja: '編集' },
  'project.team.edit.title': { ko: '스페이스 정보 수정', en: 'Edit space', ja: 'スペース情報編集' },
  'project.team.edit.err_save': { ko: '스페이스 수정 실패', en: 'Failed to update space', ja: 'スペース更新失敗' },
  'project.team.delete': { ko: '스페이스 삭제', en: 'Delete space', ja: 'スペース削除' },
  'project.team.delete.confirm': { ko: "'{name}' 스페이스를 삭제할까요?", en: "Delete space '{name}'?", ja: "スペース'{name}'を削除しますか？" },
  'project.team.delete.err_active': { ko: '활성 프로젝트가 있는 스페이스는 삭제할 수 없습니다. 프로젝트를 먼저 정리하세요.', en: 'Cannot delete a space with active projects. Archive or delete them first.', ja: '進行中プロジェクトがあるスペースは削除できません。先に整理してください。' },
  'project.team.delete.err': { ko: '스페이스 삭제 실패', en: 'Failed to delete space', ja: 'スペース削除失敗' },

  // ===== v2.3 — 삭제 컨펌 내부 내용 표시 (2026-06-11): 팀=프로젝트 존재, 프로젝트=할일 존재 =====
  'project.team.delete.projects_warn': { ko: '이 스페이스에 프로젝트 {n}개가 있습니다 — {names}', en: 'This space has {n} project(s) — {names}', ja: 'このスペースには{n}件のプロジェクトがあります — {names}' },
  'project.team.delete.projects_none': { ko: '스페이스에 프로젝트가 없습니다.', en: 'No projects in this space.', ja: 'プロジェクトはありません。' },
  'project.delete.confirm': { ko: "'{name}' 프로젝트를 삭제할까요?", en: "Delete project '{name}'?", ja: "プロジェクト'{name}'を削除しますか？" },
  'project.delete.tasks_summary': { ko: '할일 {n}개(완료 {done}) 포함 — {names}', en: 'Contains {n} task(s) ({done} done) — {names}', ja: 'タスク{n}件(完了{done})を含む — {names}' },
  'project.delete.tasks_count_only': { ko: '할일 {n}개(완료 {done}) 포함', en: 'Contains {n} task(s) ({done} done)', ja: 'タスク{n}件(完了{done})を含む' },
  'project.delete.tasks_none': { ko: '포함된 할일이 없습니다.', en: 'No tasks inside.', ja: 'タスクはありません。' },
  'project.delete.more_suffix': { ko: ' 외 {n}개', en: ' and {n} more', ja: ' 他{n}件' },
  'project.delete.err': { ko: '프로젝트 삭제 실패', en: 'Failed to delete project', ja: 'プロジェクト削除失敗' },
  'project.delete.err_forbidden': { ko: '프로젝트 삭제 권한이 없습니다 (소유자만 가능).', en: 'No permission to delete (owner only).', ja: '削除権限がありません（オーナーのみ）。' },
  'project.delete.err_not_creator': { ko: '프로젝트 삭제는 생성자만 가능합니다.', en: 'Only the project creator can delete it.', ja: '削除は作成者のみ可能です。' },
  'project.list.delete_aria': { ko: "'{name}' 프로젝트 삭제", en: "Delete project '{name}'", ja: "プロジェクト'{name}'を削除" },

  // ===== v2.5 — 팀 0 온보딩(시안②) + 팀 셋업 위저드(시안A) (2026-06-11 확정) =====
  'project.onb.heading': { ko: '3단계로 협업 시작하기', en: 'Start space collaboration in 3 steps', ja: '3ステップでスペース協業を開始' },
  'project.onb.sub': { ko: '지금은 1단계 — 스페이스 이름만 정하면 바로 시작할 수 있어요.', en: 'Step 1 now — just pick a space name to get started.', ja: '今はステップ1 — スペース名を決めるだけで始められます。' },
  'project.onb.step1.title': { ko: '스페이스 만들기', en: 'Create a space', ja: 'スペース作成' },
  'project.onb.step1.desc': { ko: '스페이스 이름과 설명만 정하면 됩니다. 1분이면 충분해요.', en: 'Name your space and create the space. Takes a minute.', ja: 'スペース名と説明を決めて空間を作ります。1分で十分。' },
  'project.onb.step2.title': { ko: '멤버 초대', en: 'Invite members', ja: 'メンバー招待' },
  'project.onb.step2.desc': { ko: '동료를 초대하고 리더·멤버 역할을 지정합니다.', en: 'Invite teammates and assign leader/member roles.', ja: '同僚を招待しリーダー・メンバーの役割を指定。' },
  'project.onb.step3.title': { ko: '프로젝트 추가', en: 'Add a project', ja: 'プロジェクト追加' },
  'project.onb.step3.desc': { ko: '프로젝트와 할일을 만들어 진행률을 한눈에 봅니다.', en: 'Create projects and tasks, track progress at a glance.', ja: 'プロジェクトとタスクで進捗を一目で。' },
  'project.onb.cta': { ko: '스페이스 만들기 시작', en: 'Start creating a space', ja: 'スペース作成を開始' },
  'project.onb.step_label': { ko: 'STEP {n}', en: 'STEP {n}', ja: 'STEP {n}' },
  'project.wiz.step1': { ko: '스페이스 생성', en: 'Create', ja: 'スペース作成' },
  'project.wiz.step2': { ko: '멤버 초대', en: 'Invite', ja: 'メンバー招待' },
  'project.wiz.step3': { ko: '프로젝트', en: 'Project', ja: 'プロジェクト' },
  'project.wiz.step1.sub': { ko: '스페이스 이름만 정하면 바로 시작할 수 있어요.', en: 'Just pick a space name to get started.', ja: 'スペース名を決めるだけで始められます。' },
  'project.wiz.step2.title': { ko: '멤버 초대', en: 'Invite members', ja: 'メンバー招待' },
  'project.wiz.step2.sub': { ko: '이름·이메일로 검색해 추가하세요. 나중에 해도 됩니다.', en: 'Search by name or email. You can do this later.', ja: '名前・メールで検索して追加。後でもOK。' },
  'project.wiz.next_invite': { ko: '다음 — 멤버 초대', en: 'Next — invite members', ja: '次へ — メンバー招待' },
  'project.wiz.next_finish': { ko: '다음 — 마무리', en: 'Next — finish', ja: '次へ — 仕上げ' },
  'project.wiz.skip': { ko: '건너뛰기', en: 'Skip', ja: 'スキップ' },
  'project.wiz.invited_n': { ko: '{n}명 초대됨', en: '{n} invited', ja: '{n}名招待済み' },
  'project.wiz.done_title': { ko: "'{name}' 스페이스가 만들어졌어요", en: "Space '{name}' is ready", ja: "スペース'{name}'ができました" },
  'project.wiz.done_sub': { ko: '멤버 {n}명과 함께 시작합니다. 첫 프로젝트도 바로 추가해 볼까요?', en: 'Starting with {n} member(s). Add your first project now?', ja: 'メンバー{n}名でスタート。最初のプロジェクトも追加しますか？' },
  'project.wiz.project_label': { ko: '첫 프로젝트 이름 (선택)', en: 'First project name (optional)', ja: '最初のプロジェクト名（任意）' },
  'project.wiz.project_placeholder': { ko: '예: WorkChat 2.0 리뉴얼', en: 'e.g. WorkChat 2.0 renewal', ja: '例: WorkChat 2.0 リニューアル' },
  'project.wiz.later': { ko: '나중에 할게요', en: 'Maybe later', ja: '後にします' },
  'project.wiz.create_project': { ko: '프로젝트 만들고 시작', en: 'Create & start', ja: '作成して開始' },
  'project.wiz.err_project': { ko: '프로젝트 생성 실패', en: 'Failed to create project', ja: 'プロジェクト作成失敗' },
  // v2.5.1 — 위저드 버튼 정리(짧은 라벨, 단계 맥락은 헤더 노드가 전달)
  'project.wiz.next': { ko: '다음', en: 'Next', ja: '次へ' },
  'project.wiz.desc_placeholder': { ko: '스페이스의 목적이나 담당 영역 (선택)', en: 'Purpose or scope of the space (optional)', ja: 'スペースの目的や担当領域（任意）' },
  'project.wiz.required': { ko: '필수', en: 'Required', ja: '必須' },
  // v2.5.3 — 초대 시 역할 지정
  'project.wiz.role_aria': { ko: "'{name}' 초대 역할", en: "Invite role for {name}", ja: "{name}の招待役割" },

  // ===== v2.6 — 새 프로젝트 모달 상세화 (2026-06-11): 섹션 그룹 + 빠른 마감일 + 규격 Select =====
  'project.create.section_basic': { ko: '기본 정보', en: 'Basic info', ja: '基本情報' },
  'project.create.section_meta': { ko: '소속 · 일정', en: 'Space & schedule', ja: '所属・日程' },
  'project.create.name_placeholder': { ko: '예: WorkChat 2.0 리뉴얼', en: 'e.g. WorkChat 2.0 renewal', ja: '例: WorkChat 2.0 リニューアル' },
  'project.create.desc_placeholder': { ko: '프로젝트의 목적이나 범위 (선택)', en: 'Purpose or scope of the project (optional)', ja: 'プロジェクトの目的や範囲（任意）' },
  'project.create.due_quick_1w': { ko: '+1주', en: '+1 wk', ja: '+1週' },
  'project.create.due_quick_2w': { ko: '+2주', en: '+2 wks', ja: '+2週' },
  'project.create.due_quick_1m': { ko: '+1개월', en: '+1 mo', ja: '+1ヶ月' },
  'project.create.due_clear': { ko: '지우기', en: 'Clear', ja: 'クリア' },
  'project.create.due_quick_aria': { ko: '마감일 빠른 선택', en: 'Quick due date', ja: '期限のクイック選択' },
  'project.create.btn_create': { ko: '프로젝트 만들기', en: 'Create project', ja: 'プロジェクト作成' },
  'project.create.name_counter_aria': { ko: '이름 글자수', en: 'Name length', ja: '名前の文字数' },
  // v2.9 — 제목 링크 (새 창)
  'project.create.link_toggle': { ko: '제목에 링크 연결 (새 창으로 열기)', en: 'Link the title (opens in new tab)', ja: 'タイトルにリンク（新しいタブ）' },
  'project.create.link_label': { ko: '링크 URL', en: 'Link URL', ja: 'リンクURL' },
  'project.create.link_placeholder': { ko: 'https://example.com', en: 'https://example.com', ja: 'https://example.com' },
  'project.create.link_err': { ko: 'http:// 또는 https:// 로 시작하는 URL을 입력하세요.', en: 'Enter a URL starting with http:// or https://.', ja: 'http:// または https:// で始まるURLを入力してください。' },

  // ══ 간단 생성 모달(ProjectCreateSimpleModal) — 정본에서 그대로 (2026-10-04 · 조각 ⓒ) ══
  //
  // ⚠ 위의 `project.create.*` 열쇠들은 ★ 「옛 모달」(ProjectCreateModal)의 것이고 ⛔ 지우지 않는다 —
  //   그 모달은 보존한다(소스 회귀 금지). 롤백은 호출부의 import 한 줄이다.
  // ⚠ 그래서 ★ 같은 앞머리에 «두 벌»이 산다. 가르는 법:
  //     옛 것: label_name · label_desc · label_due · section_* · due_quick_* · link_* · btn_create
  //     새 것: simple_title · color_* · name_ph · location* · vis* · confirm   ← 아래 13개
  //   ⇒ ★ 둘이 «같이» 쓰는 것은 ⭐ err_name · err_save 둘이다(그래서 아래에 다시 적지 않는다).
  // ⚠ 값은 ★ 정본(origin/feature/dev-syk)에서 ⭐ «글자까지 그대로» 가져왔다(2026-10-04 대조).
  'project.create.simple_title': { ko: '프로젝트 생성', en: 'Create project', ja: 'プロジェクト作成' },
  'project.create.color_and_name': { ko: '색상 및 프로젝트명', en: 'Color and name', ja: '色とプロジェクト名' },
  'project.create.color_pick': { ko: '색상 선택', en: 'Pick a color', ja: '色を選択' },
  'project.create.name_ph': { ko: '프로젝트명을 입력하세요', en: 'Enter a project name', ja: 'プロジェクト名を入力' },
  'project.create.location': { ko: '프로젝트 위치', en: 'Location', ja: 'プロジェクトの場所' },
  /*
   * ⏳ 이 열쇠는 «걷힐 예정»이다 — 2026-10-12 사용자 결정 ⇒ 「프로젝트는 무조건 스페이스 안에
   *   있어야 합니다」. ⇒ 그 결정이 구현되면 「개인(스페이스 없음)」 자리가 사라지고
   *   이 열쇠도 함께 없어진다.
   * ⇒ 만료 조건 ⇒ 이 열쇠를 부르는 제품 파일이 0 이 되면 지운다.
   */
  // ⛔ 2026-10-12 — 'project.create.location_personal'(「개인 (스페이스 없음)」)을 걷었다.
  //   사용자 결정 ⇒ 「프로젝트는 무조건 스페이스가 있어야한다」 ⇒ 그 선택지가 사라졌다.
  //   ⇒ 쓰던 곳은 ProjectCreateSimpleModal 하나였고 그 줄도 함께 걷었다.
  'project.create.need_space': { ko: '스페이스를 먼저 만들어 주세요', en: 'Create a space first', ja: 'スペースを先に作成してください' },
  'project.create.need_space_hint': { ko: '프로젝트는 스페이스 안에 만듭니다. 왼쪽 메뉴의 [새 스페이스 만들기]로 하나 만든 뒤 다시 시도해 주세요.', en: 'Projects live inside a space. Create one with [New space] in the left menu, then try again.', ja: 'プロジェクトはスペース内に作成します。左メニューの[新しいスペース]から作成してお試しください。' },
  'project.create.visibility': { ko: '프로젝트 유형', en: 'Project type', ja: 'プロジェクト種別' },
  'project.create.vis_team': { ko: '공개 프로젝트', en: 'Public project', ja: '公開プロジェクト' },
  'project.create.vis_private': { ko: '비공개 프로젝트', en: 'Private project', ja: '非公開プロジェクト' },
  // ⚠ 이 셋은 ★ 「누가 보나」를 말한다 — ⛔ 화면이 정하는 것이 아니라 «서버 규칙»을 옮긴 말이다.
  //   schema.prisma 의 그 주석(「PRIVATE = 생성자만」)과 같은 뜻으로 두었다.
  'project.create.vis_hint_team': {
    ko: '스페이스 멤버가 프로젝트 멤버로 함께 등재됩니다',
    en: 'Space members join the project as members',
    ja: 'スペースメンバーがプロジェクトメンバーとして登録されます',
  },
  'project.create.vis_hint_private': {
    ko: '스페이스에 속하지만 멤버는 나만 — 초대해야 보입니다',
    en: 'Belongs to the space, but only you are a member until you invite',
    ja: 'スペースに属しますがメンバーは自分だけ — 招待が必要です',
  },
  'project.create.vis_hint_personal': {
    ko: '스페이스 없이 나만 쓰는 프로젝트입니다',
    en: 'A personal project with no space',
    ja: 'スペースなしの個人プロジェクトです',
  },
  'project.create.confirm': { ko: '확인', en: 'Create', ja: '確認' },

  'project.team.member.remove': { ko: '제거', en: 'Remove', ja: '除外' },
  'project.team.member.leave': { ko: '스페이스 탈퇴', en: 'Leave space', ja: 'スペース脱退' },
  'project.team.member.leave_confirm': { ko: '이 스페이스에서 탈퇴할까요?', en: 'Leave this space?', ja: 'このスペースから脱退しますか？' },
  'project.team.member.remove_confirm': { ko: '{name} 님을 스페이스에서 제거할까요?', en: 'Remove {name} from the space?', ja: '{name}さんをスペースから除外しますか？' },
  'project.team.member.err_last_leader': { ko: '마지막 리더는 변경·제거할 수 없습니다. 다른 리더를 먼저 지정하세요.', en: 'The last leader cannot be changed or removed. Assign another leader first.', ja: '最後のリーダーは変更・除外できません。先に他のリーダーを指定してください。' },
  'project.team.member.err': { ko: '멤버 변경 실패', en: 'Failed to update member', ja: 'メンバー更新失敗' },
  'project.team.role_change_aria': { ko: '{name} 역할 변경', en: 'Change role of {name}', ja: '{name}の役割変更' },

  // ===== v2.1 — 팀 상세 시각 강화 (2026-06-10, C안: 진행률 링 + 아바타 스택 + 설정 드롭다운) =====
  'project.team.settings_aria': { ko: '스페이스 설정', en: 'Space settings', ja: 'スペース設定' },
  'project.team.detail.avg_progress_aria': { ko: '스페이스 평균 진행률 {percent}%', en: 'Space average progress {percent}%', ja: 'スペース平均進捗 {percent}%' },
  'project.team.detail.projects_heading': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'project.team.detail.no_projects': { ko: '이 스페이스의 프로젝트가 없습니다.', en: 'No projects in this space.', ja: 'このスペースのプロジェクトはありません。' },
  'project.team.detail.more_members_aria': { ko: '외 {n}명', en: '+{n} more', ja: '他{n}名' },

  // ===== v2 — 프로젝트 상세 =====
  'project.detail.projects.filter_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'project.detail.projects.team_filter_aria': { ko: '스페이스 필터', en: 'Space filter', ja: 'スペースフィルター' },

  // ===== v2 — 내 할일 상세 =====
  'project.detail.mytasks.tab_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'project.detail.mytasks.more': { ko: '더 보기', en: 'Load more', ja: 'もっと見る' },
  'project.detail.mytasks.total': { ko: '총 {n}건', en: '{n} total', ja: '計 {n}件' },
  'project.detail.mytasks.empty': { ko: '할일이 없습니다.', en: 'No tasks.', ja: 'タスクがありません。' },
  'project.detail.mytasks.tabs_aria': { ko: '상태 필터', en: 'Status filter', ja: 'ステータスフィルター' },

  // ===== v2 — 통계 상세 =====
  'project.detail.stats.weekly_gauge': { ko: '주간 완료율', en: 'Weekly done rate', ja: '週間完了率' },
  'project.detail.stats.weekly_gauge_none': { ko: '최근 7일 데이터 없음', en: 'No data in last 7 days', ja: '直近7日のデータなし' },
  'project.detail.stats.by_status': { ko: '상태 분포', en: 'By status', ja: 'ステータス分布' },
  'project.detail.stats.due_soon_heading': { ko: '7일 내 마감', en: 'Due in 7 days', ja: '7日以内期限' },
  'project.detail.stats.overdue_heading': { ko: '지연', en: 'Overdue', ja: '遅延' },
  'project.detail.stats.empty_list': { ko: '해당 없음', en: 'None', ja: '該当なし' },
  'project.detail.stats.err': { ko: '통계 조회 실패', en: 'Failed to load stats', ja: '統計取得失敗' },

  // ===== v2.10 — 통계 상세화 (KPI 스트립 + D-day 배지) =====
  'project.detail.stats.kpi_aria': { ko: '통계 요약', en: 'Stats summary', ja: '統計サマリー' },
  'project.detail.stats.kpi_total': { ko: '전체 할일', en: 'All tasks', ja: '全タスク' },
  'project.detail.stats.dday_today': { ko: 'D-DAY', en: 'D-day', ja: '当日' },
  'project.detail.stats.dday_left': { ko: 'D-{n}', en: 'D-{n}', ja: 'D-{n}' },
  'project.detail.stats.dday_over': { ko: 'D+{n}', en: 'D+{n}', ja: 'D+{n}' },

  // ===== v2.10.1 — 내 할일 상세화 (진행 요약 밴드 + 탭 카운트) =====
  'project.detail.mytasks.hero_aria': { ko: '내 할일 진행 요약', en: 'My tasks progress summary', ja: 'マイタスク進行サマリー' },
  'project.detail.mytasks.hero_done': { ko: '완료 {done} / 전체 {total}', en: 'Done {done} / {total}', ja: '完了 {done} / 全 {total}' },

  // ===== v2.10.2 — 프로젝트 목록 상세화 (팀 칩 + 완료 집계) =====
  'project.list.done_count': { ko: '완료 {n}', en: 'Done {n}', ja: '完了 {n}' },
  'project.list.personal_chip': { ko: '개인', en: 'Personal', ja: '個人' },

  // ===== AW-001 프로젝트메인 개선안 (화면설계서 v2.2 · 2026-08-18) =====
  'project.home.aw001.hero_aria': { ko: '오늘의 현황', en: 'Today at a glance', ja: '本日の状況' },
  'project.home.aw001.greeting_morning': { ko: '좋은 아침이에요, {name}님', en: 'Good morning, {name}', ja: 'おはようございます、{name}さん' },
  'project.home.aw001.greeting_afternoon': { ko: '좋은 오후예요, {name}님', en: 'Good afternoon, {name}', ja: 'こんにちは、{name}さん' },
  'project.home.aw001.greeting_evening': { ko: '좋은 저녁이에요, {name}님', en: 'Good evening, {name}', ja: 'こんばんは、{name}さん' },
  'project.home.aw001.stat_due_soon': { ko: '마감임박', en: 'Due soon', ja: '期限間近' },
  'project.home.aw001.stat_overdue': { ko: '지연', en: 'Overdue', ja: '遅延' },
  'project.home.aw001.stat_risk': { ko: '위험', en: 'At risk', ja: 'リスク' },
  'project.home.aw001.urgent': { ko: '가장 급한 건 {space} 스페이스의 {project} ({dday})예요', en: 'Most urgent: {project} in {space} ({dday})', ja: '最優先は {space} の {project}（{dday}）です' },
  'project.home.aw001.urgent_none': { ko: '급한 마감이 없어요.', en: 'Nothing urgent right now.', ja: '差し迫った期限はありません。' },
  'project.home.aw001.urgent_go': { ko: '지금 보기', en: 'View now', ja: '今すぐ見る' },
  'project.home.aw001.search_placeholder': { ko: '프로젝트 통합 검색', en: 'Search all projects', ja: 'プロジェクト統合検索' },
  'project.home.aw001.search_hint': { ko: '프로젝트명 · 담당자 · 태스크로 찾기', en: 'By project, owner, or task', ja: 'プロジェクト名・担当者・タスクで検索' },
  'project.home.aw001.spaces_heading': { ko: '내 스페이스', en: 'My spaces', ja: 'マイスペース' },
  'project.home.aw001.sort_recent': { ko: '최근 생성순', en: 'Newest first', ja: '作成が新しい順' },
  'project.home.aw001.sort_name': { ko: '이름순', en: 'By name', ja: '名前順' },
  'project.home.aw001.sort_aria': { ko: '정렬 기준 변경', en: 'Change sort order', ja: '並び順を変更' },
  'project.home.aw001.space_meta': { ko: '멤버 {m}명 · 프로젝트 {p}개', en: '{m} members · {p} projects', ja: 'メンバー {m}人 · プロジェクト {p}件' },
  'project.home.aw001.space_overdue': { ko: '지연 {n}건', en: '{n} overdue', ja: '遅延 {n}件' },
  'project.home.aw001.space_progress_aria': { ko: '진행률 {n}퍼센트', en: '{n} percent complete', ja: '進捗 {n}パーセント' },
  'project.home.aw001.space_open_aria': { ko: '{name} 스페이스 열기', en: 'Open space {name}', ja: 'スペース {name} を開く' },
  'project.home.aw001.new_space': { ko: '새 스페이스 만들기', en: 'New space', ja: '新規スペース' },
  'project.home.aw001.empty': { ko: '진행 중인 스페이스가 없어요. 새 스페이스를 만들어 시작하세요.', en: 'No active spaces yet. Create one to get started.', ja: '進行中のスペースがありません。新規作成して始めましょう。' },
  'project.home.aw001.search_empty': { ko: '검색과 일치하는 스페이스가 없어요.', en: 'No spaces match your search.', ja: '検索に一致するスペースがありません。' },
  'project.home.aw001.dday_today': { ko: 'D-DAY', en: 'D-day', ja: '当日' },
  'project.home.aw001.dday_left': { ko: 'D-{n}', en: 'D-{n}', ja: 'D-{n}' },
  'project.home.aw001.dday_over': { ko: 'D+{n}', en: 'D+{n}', ja: 'D+{n}' },

  // ===== 공통 버튼 =====
  'project.btn.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'project.btn.save': { ko: '저장', en: 'Save', ja: '保存' },
  'project.btn.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'project.btn.close': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'project.btn.retry': { ko: '다시 시도', en: 'Retry', ja: '再試行' },

  /*
    ── 내 할일 «행 부가정보» (my-task-due · MyTasksRowExtras · 2026-10-01) ──

    ⭐ 2026-10-01 사용자 승인으로 «되돌림을 풀었다». 이 열다섯은 그 두 부품이 «부르는 것»이고,
      정본(feature/dev-syk)의 글자 그대로다 — 바꾸면 「같은 뜻을 다른 글자로」가 되고
      정본 시험이 그 글자를 재고 있다.
    ⚠ 정본 사전의 `project.mytask.*` 는 «스물다섯»인데 여기는 «열다섯»이다 —
      나머지 열(due_pick* · due_q_* · due_clear · due_apply · due_on)은 «기한 고르개»
      (MyTaskDuePicker)가 부르는 것이고 그 부품은 이 판에 없다. 부르는 곳 없는 키는 안 넣는다.
    ⚠ 그리고 이 수를 «좁게 세면 13 이 나온다» — act_pri_on/off 는 `tProject('…')` 리터럴이
      아니라 삼항으로 골라 부르기 때문이다. 앞가지(`project.mytask.`)로 훑어야 열다섯이다.
  */
  'project.mytask.gsum_overdue': { ko: '지연 {n}', en: '{n} overdue', ja: '遅延 {n}' },
  'project.mytask.gsum_soonest_over': { ko: '최장 {n}일 지연', en: 'up to {n}d overdue', ja: '最長 {n}日 遅延' },
  'project.mytask.gsum_soonest_left': { ko: '가장 급한 건 {n}일 남음', en: 'soonest in {n}d', ja: '最短 あと{n}日' },
  'project.mytask.gsum_avg': { ko: '평균 진척률 {n}퍼센트', en: 'average progress {n} percent', ja: '平均進捗 {n}パーセント' },
  'project.mytask.act_pri_on': { ko: '우선순위 높음으로', en: 'Mark high priority', ja: '優先度を高に' },
  'project.mytask.act_pri_off': { ko: '우선순위 보통으로', en: 'Mark medium priority', ja: '優先度を中に' },
  'project.mytask.act_postpone': { ko: '기한 정하기', en: 'Set due date', ja: '期限を決める' },
  'project.mytask.act_open': { ko: '업무 상세 열기', en: 'Open task detail', ja: 'タスク詳細を開く' },
  'project.mytask.act_delete': { ko: '업무 삭제', en: 'Delete task', ja: '業務を削除' },
  'project.mytask.act_delete_aria': { ko: "'{title}' 업무 삭제", en: "Delete task '{title}'", ja: "'{title}' を削除" },
  'project.mytask.due_done': { ko: '완료', en: 'Done', ja: '完了' },
  'project.mytask.due_overdue': { ko: '{n}일 지연', en: '{n} days overdue', ja: '{n}日遅延' },
  'project.mytask.due_today': { ko: '오늘 마감', en: 'Due today', ja: '本日締切' },
  'project.mytask.due_tomorrow': { ko: '내일 마감', en: 'Due tomorrow', ja: '明日締切' },
  'project.mytask.due_left': { ko: '{n}일 남음', en: '{n} days left', ja: '残り{n}日' },

  // ===== 스페이스 상세 (2차 2-1′ · 2026-10-08) =====
  //   ⭐ 아래 35줄은 정본(feature/dev-syk)의 선언을 ★ «글자 그대로» 옮긴 것이다.
  //   ⛔ 손으로 적지 않았다 — 한글 손 입력은 딴 말을 만든다(그 함정은 적혀 있다).
  //   ⚠ 이 열쇠는 ★ 깃발 PROJECT_V2 가 «켜질 때»만 쓰인다 ⇒ 꺼진 동안 ⭐ 보이는 글자 0.
  'project.info.desc': { ko: '설명', en: 'Description', ja: '説明' },
  'project.info.no_members': { ko: '표시할 멤버가 없습니다.', en: 'No members to show.', ja: '表示するメンバーがいません。' },
  'project.space.back': { ko: '프로젝트 홈', en: 'Project home', ja: 'プロジェクトホーム' },
  'project.space.chart': { ko: '프로젝트 상태 현황', en: 'Project status', ja: 'プロジェクト状況' },
  'project.space.chart_aria': { ko: '전체 {total}개 중 완료 {done}개', en: '{done} of {total} done', ja: '全{total}件中 完了{done}件' },
  'project.space.chart_unit': { ko: '프로젝트', en: 'projects', ja: 'プロジェクト' },
  'project.space.col_due': { ko: '마감일', en: 'Due', ja: '期限' },
  'project.space.col_name': { ko: '프로젝트명', en: 'Project', ja: 'プロジェクト名' },
  'project.space.col_progress': { ko: '진척률', en: 'Progress', ja: '進捗' },
  'project.space.col_role': { ko: '내 권한', en: 'My role', ja: '自分の権限' },
  'project.space.col_start': { ko: '시작일', en: 'Start', ja: '開始日' },
  'project.space.col_status': { ko: '진행 상태', en: 'Status', ja: '進行状態' },
  'project.space.copy_link': { ko: '링크 복사', en: 'Copy link', ja: 'リンクをコピー' },
  'project.space.desc_placeholder': {
    ko: '스페이스에 대한 설명을 작성하세요',
    en: 'Describe what this space is for',
    ja: 'スペースの説明を記入しましょう',
  },
  'project.space.empty': {
    ko: '이 스페이스에는 아직 프로젝트가 없습니다. [프로젝트 생성] 으로 시작하세요.',
    en: 'No projects in this space yet. Start with “New project”.',
    ja: 'このスペースにはまだプロジェクトがありません。',
  },
  'project.space.err_fetch': { ko: '스페이스를 불러오지 못했습니다.', en: 'Could not load the space.', ja: 'スペースを読み込めませんでした。' },
  'project.space.filter_mine': { ko: '내가 참여한 것만', en: 'Only mine', ja: '参加中のみ' },
  'project.space.info': { ko: '스페이스 정보', en: 'Space info', ja: 'スペース情報' },
  'project.space.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'project.space.me': { ko: '나', en: 'me', ja: '自分' },
  'project.space.members': { ko: '멤버 {n}명', en: '{n} members', ja: 'メンバー{n}名' },
  'project.space.more': { ko: '더 보기', en: 'More', ja: 'その他' },
  'project.space.new_project': { ko: '프로젝트 생성', en: 'New project', ja: 'プロジェクト作成' },
  'project.space.role_manager': { ko: '매니저', en: 'Manager', ja: 'マネージャー' },
  'project.space.role_member': { ko: '멤버', en: 'Member', ja: 'メンバー' },
  'project.space.role_none': { ko: '미참여', en: 'Not joined', ja: '未参加' },
  'project.space.role_owner': { ko: '마스터', en: 'Owner', ja: 'マスター' },
  'project.space.role_viewer': { ko: '뷰어', en: 'Viewer', ja: 'ビューア' },
  'project.space.settings': { ko: '스페이스 설정', en: 'Space settings', ja: 'スペース設定' },
  'project.space.sort': { ko: '정렬 기준', en: 'Sort by', ja: '並び順' },
  'project.space.sort_due': { ko: '정렬 · 마감임박순', en: 'Sort · due soon', ja: '並び順 · 期限' },
  'project.space.sort_name': { ko: '정렬 · 이름순', en: 'Sort · name', ja: '並び順 · 名前' },
  'project.space.sort_progress': { ko: '정렬 · 진척률순', en: 'Sort · progress', ja: '並び順 · 進捗' },
  'project.space.status_active': { ko: '진행 중', en: 'In progress', ja: '進行中' },
  'project.space.status_done': { ko: '완료', en: 'Done', ja: '完了' },

  // ===== 체크리스트 서식 (2차 2-2′ · 2026-10-08) =====
  //   ⭐ 정본(feature/dev-syk)의 선언을 ★ «괄호 맞춰» 그대로 옮겼다 — ⛔ 손으로 적지 않았다.
  //   ⚠ 깃발 PROJECT_V2 가 «켜질 때»만 쓰인다 ⇒ 꺼진 동안 ⭐ 보이는 글자 0.
  'project.tmpl.delete': { ko: '템플릿 삭제', en: 'Delete template', ja: 'テンプレート削除' },
  'project.tmpl.edit': { ko: '템플릿 편집', en: 'Edit template', ja: 'テンプレート編集' },
  'project.tmpl.empty': { ko: '이 범위에는 템플릿이 없습니다.', en: 'No templates in this scope.', ja: 'この範囲にテンプレートがありません。' },
  'project.tmpl.err_delete': { ko: '템플릿 삭제 실패', en: 'Failed to delete template', ja: 'テンプレート削除失敗' },
  'project.tmpl.err_name': { ko: '템플릿 이름을 입력하세요.', en: 'Please enter a template name.', ja: 'テンプレート名を入力してください。' },
  'project.tmpl.err_save': { ko: '템플릿 저장 실패', en: 'Failed to save template', ja: 'テンプレート保存失敗' },
  // 저장 줄 상태 글(2026-10-18 · web 레인 W8b · SaveBar)
  'project.tmpl.unchanged': { ko: '바뀐 것이 없습니다', en: 'No changes', ja: '変更はありません' },
  'project.tmpl.unsaved': { ko: '{n}곳 바뀜 · 저장하지 않았습니다', en: '{n} unsaved change(s)', ja: '{n}件の変更 · 未保存' },
  // 꺼진 저장 옆 «켜지는 조건»(2026-10-18 · web 레인 W8c)
  'project.tmpl.enable_hint': { ko: '칸을 고치면 켜집니다', en: 'Edit a field to enable', ja: '項目を編集すると有効になります' },
  'project.tmpl.heading': { ko: '체크리스트 템플릿', en: 'Checklist templates', ja: 'チェックリストテンプレート' },
  'project.tmpl.item_delete_aria': { ko: "'{name}' 항목 삭제", en: "Delete item '{name}'", ja: "'{name}'項目を削除" },
  'project.tmpl.item_ph': { ko: '항목 입력하고 Enter', en: 'Add an item and press Enter', ja: '項目を入力してEnter' },
  'project.tmpl.items_label': { ko: '기본 항목 · 순서 변경 가능', en: 'Default items · reorderable', ja: '既定項目 · 並び替え可' },
  'project.tmpl.items_n': { ko: '기본 {n}항목', en: '{n} items', ja: '既定{n}項目' },
  'project.tmpl.move_down': { ko: '아래로 이동', en: 'Move down', ja: '下へ' },
  'project.tmpl.move_up': { ko: '위로 이동', en: 'Move up', ja: '上へ' },
  'project.tmpl.name_label': { ko: '템플릿 이름', en: 'Template name', ja: 'テンプレート名' },
  'project.tmpl.name_ph': { ko: '예: 버그 수정 체크리스트', en: 'e.g. Bug fix checklist', ja: '例: バグ修正チェックリスト' },
  'project.tmpl.new': { ko: '새 템플릿 만들기', en: 'New template', ja: '新規テンプレート' },
  'project.tmpl.private': { ko: '개인', en: 'Private', ja: '個人' },
  'project.tmpl.scope_label': { ko: '공개 범위', en: 'Visibility', ja: '公開範囲' },
  'project.tmpl.scope_private': { ko: '개인 (나만 사용)', en: 'Private (only me)', ja: '個人（自分のみ）' },
  'project.tmpl.scope_shared': { ko: '공용 (스페이스 전체가 사용)', en: 'Shared (whole space)', ja: '共用（スペース全体）' },
  'project.tmpl.shared': { ko: '공용', en: 'Shared', ja: '共用' },
  'project.tmpl.sub': {
    ko: '업무 상세의 템플릿에서 가져오기에 나타나는 기본 서식을 만들고 관리합니다',
    en: 'Create the presets that appear in a task detail panel',
    ja: 'タスク詳細のテンプレートから に出る既定書式を管理します',
  },
  'project.tmpl.warn_empty': {
    ko: '기본 항목이 없습니다 — 항목을 넣어야 업무에서 가져올 수 있습니다.',
    en: 'No default items yet — add some so tasks can import them.',
    ja: '既定項目がありません — 追加するとタスクから取り込めます。',
  },

  // ===== 업무 묶음 (2차 4-2 · 2026-10-08) =====
  //   ⭐ 정본(feature/dev-syk)의 선언을 ★ «괄호 맞춰» 그대로 옮겼다 — ⛔ 손으로 적지 않았다.
  //   ⚠ 이 열쇠는 ★ 깃발 PROJECT_V2 가 «켜질 때»만 쓰인다 ⇒ 꺼진 동안 ⭐ 보이는 글자 0.
  //   ⚠ 통이 ★ project-i18n 이다 — ⭐ 그 둘이 `tProject` 를 쓴다(⛔ 4-1 의 task-i18n 과 ★ 다르다).
  'project.group.color_hint': {
    ko: '그룹 구분용 색상입니다 · 상태(위험도) 색과 별개로 관리',
    en: 'For grouping only · separate from status/risk colors',
    ja: 'グループ識別用の色です · 状態（リスク）色とは別管理',
  },
  'project.group.color_label': { ko: '색상', en: 'Color', ja: '色' },
  'project.group.delete_aria': { ko: "'{name}' 그룹 삭제", en: "Delete group '{name}'", ja: "'{name}' グループを削除" },
  'project.group.delete_confirm': { ko: "'{name}' 그룹을 삭제할까요?", en: "Delete group '{name}'?", ja: "'{name}' グループを削除しますか？" },
  'project.group.delete_keeps_tasks': {
    ko: '묶음만 없어지고 업무 {n}건은 그대로 남습니다.',
    en: 'Only the grouping is removed - the {n} tasks stay.',
    ja: 'グループ分けのみ解除され、タスク {n}件はそのまま残ります。',
  },
  'project.group.err_delete': { ko: '그룹 삭제 실패', en: 'Failed to delete group', ja: 'グループ削除失敗' },
  'project.group.err_name': { ko: '그룹 이름을 입력하세요.', en: 'Please enter a group name.', ja: 'グループ名を入力してください。' },
  'project.group.err_save': { ko: '그룹 생성 실패', en: 'Failed to create group', ja: 'グループ作成失敗' },
  'project.group.manage_empty': {
    ko: '아직 만든 그룹이 없습니다. [그룹 추가] 로 먼저 만들어 주세요.',
    en: 'No groups yet. Create one with [Add group].',
    ja: 'グループがまだありません。[グループ追加] から作成してください。',
  },
  'project.group.manage_loading': { ko: '그룹을 불러오는 중…', en: 'Loading groups…', ja: 'グループを読み込み中…' },
  'project.group.manage_title': { ko: '내 그룹 관리', en: 'Manage my groups', ja: 'マイグループの管理' },
  'project.group.name_label': { ko: '그룹 이름', en: 'Group name', ja: 'グループ名' },
  'project.group.name_ph': { ko: '예: 마케팅 캠페인', en: 'e.g. Marketing campaign', ja: '例: マーケティング施策' },
  'project.group.rename_aria': { ko: "'{name}' 그룹 이름", en: "Name of group '{name}'", ja: "'{name}' グループ名" },
  'project.group.rename_submit': { ko: '저장', en: 'Save', ja: '保存' },
  'project.group.submit': { ko: '그룹 만들기', en: 'Create group', ja: 'グループ作成' },
  'project.group.task_count': { ko: '업무 {n}건', en: '{n} tasks', ja: 'タスク {n}件' },
  'project.group.title': { ko: '새 그룹 추가', en: 'New group', ja: '新しいグループ' },

  // ===== 내 할일 «보조 위젯» (2차 4-3 · 2026-10-08) =====
  //   ⭐ 아래 스물셋은 정본(feature/dev-syk)의 선언을 ★ «글자 그대로» 옮긴 것이다 —
  //     MyTasksInsights · MyTasksNextUpTable · MyTasksSummary 가 ⭐ «부르는 것»만 담았다.
  //   ⚠⚠ 위 「행 부가정보」 묶음 머리말의 ★ 「여기는 «열다섯»이다」는 ⇒ ⭐ «그 묶음»의 수다 —
  //     ⇒ 이 판이 23 을 더해 ★ 이 파일의 `project.mytask.*` 는 ⇒ ⭐ «서른여덟»이 된다.
  //   ⚠ 그리고 그 머리말의 ★ 「정본 사전의 project.mytask.* 는 «스물다섯»」은 ⇒ ⛔ 좁게 센 수다 —
  //     ⇒ 재 보니 정본의 그 앞가지는 ★ «190» 이다 (2026-10-08 · 세어 본 명령):
  //       git show origin/feature/dev-syk:apps/frontend/src/lib/project-i18n.ts | grep -c "'project.mytask.\."
  //     ⇒ ⇒ 그 25 는 ★ «그 두 부품(due·act·gsum)이 부르는 묶음»의 수였다 ⇒ ⛔ 글이 수보다 넓었다.
  //   ⇒ ⭐ 그 셋을 ★ 가드가 잠근다 — pages/ProjectV2/task-widgets.guard.test.ts
  'project.mytask.chip_overdue': { ko: '지연', en: 'Overdue', ja: '遅延' },
  'project.mytask.detail_aria': { ko: "'{name}' 상세 보기", en: "Open '{name}'", ja: "'{name}'の詳細" },
  'project.mytask.done_of_total': { ko: '완료 {done} / 전체 {total}', en: '{done} done / {total} total', ja: '完了 {done} / 全体 {total}' },
  'project.mytask.insights_due_none': { ko: '미정', en: 'No date', ja: '未定' },
  'project.mytask.insights_due_title': { ko: '마감 흐름 · 앞으로 2주', en: 'Due flow · next 2 weeks', ja: '期限の流れ · 今後2週間' },
  'project.mytask.insights_empty': { ko: '그릴 만한 업무가 없어요.', en: 'Nothing to chart yet.', ja: '表示するタスクがありません。' },
  'project.mytask.insights_heading': { ko: '한눈에 보기', en: 'At a glance', ja: 'ひと目で確認' },
  'project.mytask.insights_overdue_of': { ko: '지연 {n}', en: '{n} overdue', ja: '遅延 {n}' },
  'project.mytask.insights_project_pick': { ko: '{name} 만 보기', en: 'Show only {name}', ja: '{name} だけ表示' },
  'project.mytask.insights_project_title': { ko: '프로젝트 쏠림 · 미완료 기준', en: 'Load by project · open tasks', ja: 'プロジェクト別 · 未完了' },
  'project.mytask.nextup_more': { ko: '+{n}건 더', en: '+{n} more', ja: '他{n}件' },
  'project.mytask.nextup_r1': { ko: '지금 당장', en: 'Do now', ja: '今すぐ' },
  'project.mytask.nextup_r2': { ko: '오늘 중 착수', en: 'Start today', ja: '今日中に着手' },
  'project.mytask.nextup_r3': { ko: '짬 날 때 처리', en: 'Squeeze in', ja: '空き時間で処理' },
  'project.mytask.nextup_r4': { ko: '나중에', en: 'Later', ja: '後で' },
  'project.mytask.nextup_table_click': { ko: '업무를 누르면 그 업무가 열립니다.', en: 'Click a task to open it.', ja: '業務をクリックすると開きます。' },
  'project.mytask.nextup_table_due': { ko: '기한', en: 'Due', ja: '期限' },
  'project.mytask.nextup_table_flow': {
    ko: '지연 {over} · 오늘 마감 {today} · 2주 내 {soon} · 미정 {none}',
    en: '{over} overdue · {today} due today · {soon} within 2 weeks · {none} undated',
    ja: '遅延 {over} · 本日締切 {today} · 2週間以内 {soon} · 未定 {none}',
  },
  'project.mytask.nextup_table_project': { ko: '프로젝트', en: 'Project', ja: 'プロジェクト' },
  'project.mytask.nextup_table_rank': { ko: '순위', en: 'Rank', ja: '優先' },
  'project.mytask.nextup_table_task': { ko: '업무', en: 'Task', ja: '業務' },
  'project.mytask.nextup_title': { ko: '순서대로 해야 할 일', en: 'What to do, in order', ja: '順番にやること' },
  'project.mytask.summary_heading': { ko: '요약', en: 'Summary', ja: 'サマリー' },

  // ===== 내 할일 «업무 상세 패널» (2차 4-4 · 2026-10-09) =====
  //   ⭐ 아래 49 은 정본(feature/dev-syk)의 선언을 ★ «글자 그대로» 옮긴 것이다 —
  //     MyTaskDetailPanel 이 ⭐ «부르는 것»만 담았다(그 파일에서 뽑았다).
  //   ⚠ 앞선 판(4-3)의 머리말에 적어 둔 그 수를 ★ 다시 적는다 — ⇒ ⭐ 정본의 `project.mytask.*` 는 ★ 190 이다.
  //     ⇒ ⇒ ⛔ 그러니 ★ 「정본에서 다 가져왔다」로 읽지 마십시오 — ⇒ 남은 것은 ★ 4-5 몫이다.
  //   ⇒ ⭐ 이 판의 열쇠를 ★ 가드가 잠근다 — pages/ProjectV2/task-detail.guard.test.ts
  'project.mytask.ap_approve': { ko: '승인', en: 'Approve', ja: '承認' },
  'project.mytask.ap_approve_aria': { ko: "'{name}' 완료 승인", en: "Approve '{name}'", ja: "'{name}' を承認" },
  'project.mytask.ap_err': { ko: '처리하지 못했습니다.', en: 'Could not complete the action.', ja: '処理できませんでした。' },
  'project.mytask.ap_pending': { ko: '승인 대기', en: 'Awaiting approval', ja: '承認待ち' },
  'project.mytask.ap_reject': { ko: '반려', en: 'Send back', ja: '差し戻し' },
  'project.mytask.ap_reject_aria': { ko: "'{name}' 완료 반려", en: "Send back '{name}'", ja: "'{name}' を差し戻し" },
  'project.mytask.ap_reject_confirm': { ko: '반려하기', en: 'Send back', ja: '差し戻す' },
  'project.mytask.ap_reject_ph': { ko: '반려 사유 (선택)', en: 'Reason (optional)', ja: '差し戻し理由（任意）' },
  'project.mytask.ap_rejected': { ko: '반려됨', en: 'Sent back', ja: '差し戻し' },
  'project.mytask.ap_rejected_reason': { ko: '반려 사유: {reason}', en: 'Reason: {reason}', ja: '理由: {reason}' },
  'project.mytask.ap_submit': { ko: '완료 요청', en: 'Request approval', ja: '完了申請' },
  'project.mytask.ap_submit_hint': {
    ko: '승인을 받아야 완료되는 프로젝트입니다.',
    en: 'This project requires approval to finish a task.',
    ja: 'このプロジェクトは承認を受けて完了になります。',
  },
  'project.mytask.attach_add': { ko: '파일 추가', en: 'Add file', ja: 'ファイル追加' },
  'project.mytask.attach_delete': { ko: "'{name}' 삭제", en: "Delete '{name}'", ja: "'{name}' を削除" },
  'project.mytask.attach_download': { ko: "'{name}' 내려받기", en: "Download '{name}'", ja: "'{name}' をダウンロード" },
  'project.mytask.attach_empty': { ko: '첨부 파일이 없습니다.', en: 'No attachments.', ja: '添付ファイルはありません。' },
  'project.mytask.attach_err': { ko: '첨부 파일을 처리하지 못했습니다.', en: 'Could not handle the attachment.', ja: '添付ファイルを処理できませんでした。' },
  'project.mytask.attachments': { ko: '첨부 파일 ({n})', en: 'Attachments ({n})', ja: '添付ファイル ({n})' },
  'project.mytask.blocked_hint': {
    ko: '남은 {n}개를 체크해야 완료',
    en: 'Check the remaining {n} to finish',
    ja: '残り{n}件をチェックすると完了',
  },
  'project.mytask.checklist': { ko: '체크리스트 ({done}/{total})', en: 'Checklist ({done}/{total})', ja: 'チェックリスト ({done}/{total})' },
  'project.mytask.comment_delete': { ko: '코멘트 삭제', en: 'Delete comment', ja: 'コメントを削除' },
  'project.mytask.comment_empty': { ko: '아직 코멘트가 없습니다.', en: 'No comments yet.', ja: 'コメントはまだありません。' },
  'project.mytask.comment_err': { ko: '코멘트를 등록하지 못했습니다.', en: 'Could not post the comment.', ja: 'コメントを投稿できませんでした。' },
  'project.mytask.comment_ph': { ko: '코멘트를 입력하고 Enter', en: 'Write a comment and press Enter', ja: 'コメントを入力してEnter' },
  'project.mytask.comment_send': { ko: '코멘트 등록', en: 'Post comment', ja: 'コメントを投稿' },
  'project.mytask.comments': { ko: '코멘트 ({n})', en: 'Comments ({n})', ja: 'コメント ({n})' },
  'project.mytask.confirm_left': {
    ko: '체크리스트 {n}개를 끝내야 완료할 수 있습니다.',
    en: '{n} checklist items must be done first.',
    ja: 'チェックリスト{n}件を終えないと完了できません。',
  },
  'project.mytask.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'project.mytask.delete_aria': { ko: "'{title}' 태스크 삭제", en: "Delete task '{title}'", ja: "タスク'{title}'を削除" },
  'project.mytask.delete_confirm': { ko: "'{title}' 태스크를 삭제할까요?", en: "Delete task '{title}'?", ja: "タスク'{title}'を削除しますか？" },
  'project.mytask.delete_err': { ko: '태스크 삭제 실패', en: 'Failed to delete task', ja: 'タスク削除失敗' },
  'project.mytask.delete_err_forbidden': {
    ko: '태스크 삭제 권한이 없습니다 (작성자 또는 관리자만 가능).',
    en: 'No permission to delete this task (creator or manager only).',
    ja: 'タスクの削除権限がありません（作成者または管理者のみ）。',
  },
  'project.mytask.delete_irreversible': { ko: '되돌릴 수 없습니다.', en: 'This cannot be undone.', ja: '元に戻せません。' },
  'project.mytask.description': { ko: '태스크 상세 설명', en: 'Task description', ja: 'タスクの詳細説明' },
  'project.mytask.done_but_left': {
    ko: '⚠ 완료됐지만 {n}개가 미체크입니다',
    en: '⚠ Marked done with {n} unchecked',
    ja: '⚠ 完了ですが{n}件が未チェック',
  },
  'project.mytask.due_on': { ko: '마감일 {date}', en: 'due {date}', ja: '締切 {date}' },
  'project.mytask.err_import': { ko: '체크리스트를 가져오지 못했습니다.', en: 'Could not import the checklist.', ja: 'チェックリストを取り込めませんでした。' },
  'project.mytask.extra': { ko: '추가 정보 ({n})', en: 'Additional info ({n})', ja: '追加情報 ({n})' },
  'project.mytask.extra_err': { ko: '추가 정보를 저장하지 못했습니다.', en: 'Could not save the field.', ja: '追加情報を保存できませんでした。' },
  'project.mytask.from_template': { ko: '템플릿에서 가져오기', en: 'From template', ja: 'テンプレートから' },
  'project.mytask.group_label': { ko: '그룹', en: 'Group', ja: 'グループ' },
  'project.mytask.group_none': { ko: '미분류', en: 'Ungrouped', ja: '未分類' },
  'project.mytask.import': { ko: '가져오기 +', en: 'Import +', ja: '取り込み +' },
  'project.mytask.import_empty': {
    ko: "'{name}' 에는 기본 항목이 없습니다. 체크리스트 템플릿 화면에서 항목을 먼저 넣어 주세요.",
    en: "'{name}' has no default items. Add items on the checklist template page first.",
    ja: "'{name}' に既定項目がありません。テンプレート管理画面で項目を追加してください。",
  },
  'project.mytask.item_delete_aria': { ko: "'{name}' 항목 삭제", en: "Delete item '{name}'", ja: "'{name}'項目を削除" },
  'project.mytask.item_ph': { ko: '체크리스트 항목 입력하고 Enter', en: 'Add an item and press Enter', ja: '項目を入力してEnter' },
  'project.mytask.open_project': { ko: '프로젝트에서 보기', en: 'Open in project', ja: 'プロジェクトで見る' },
  'project.mytask.template_empty': { ko: '만들어 둔 템플릿이 없습니다.', en: 'No templates yet.', ja: 'テンプレートがありません。' },
  'project.mytask.template_hint': {
    ko: '템플릿을 펼치면 기본 항목이 보이고, 가져오기로 한 번에 추가됩니다',
    en: 'Expand a template to preview its items, then import them at once',
    ja: 'テンプレートを開くと既定項目が見え、取り込みで一括追加されます',
  },

  //   ⚠⚠ 아래 둘은 ★ «삼항»으로 골라 부르는 열쇠다 — ⇒ MyTaskDetailPanel 의 그 줄:
  //     {tProject(status === 'DONE' ? 'project.mytask.undo_done' : 'project.mytask.mark_done')}
  //   ⇒ ⛔ 그래서 ★ `tProject('…')` 꼴만 보는 뽑개가 ⇒ ⭐ 이 둘을 «놓쳤다».
  //     ⇒ ⇒ ★ 그 결과 ⭐ 정본 시험 하나가 ★ 「완료 처리 단추를 못 찾는다」로 ⇒ 빨개졌다(그것이 잡아 줬다).
  //   ⇒ ⚠ 이 파일 위쪽 「행 부가정보」 머리말이 ★ 그 함정을 ⭐ 이미 적어 두었다 —
  //     「act_pri_on/off 는 `tProject('…')` 리터럴이 아니라 삼항으로 골라 부르기 때문이다」
  //     ⇒ ⇒ ⛔ 적어 둔 것을 ★ 다시 밟았다. ⇒ ⭐ 이제 뽑개가 «인자를 괄호 균형으로 잘라» 센다.
  'project.mytask.mark_done': { ko: '완료 처리', en: 'Mark done', ja: '完了にする' },
  'project.mytask.undo_done': { ko: '진행 중으로 되돌리기', en: 'Reopen task', ja: '進行中に戻す' },

  // ===== 내 할일 «보드 ＋ 배선» (2차 4-5 · 2026-10-09) =====
  //   ⭐ 아래 70 은 정본(feature/dev-syk)의 선언을 ★ «글자 그대로» 옮긴 것이다 —
  //     MyTasksBoard · MyTaskCreateModal · MyTaskDuePicker 가 ⭐ «부르는 것»만 담았다.
  //   ⚠⚠ 이 판의 뽑개는 ★ 네 꼴을 ⭐ «다» 센다 — ⇒ 앞선 판에서 ★ 삼항을 놓쳐 ⭐ 시험이 빨개졌다:
  //     ⓐ 리터럴  t('a.b')                     ⓑ 삼항   t(c ? 'a.b' : 'a.c')
  //     ⓒ 런타임 조립 t(`a.b_${v}` as never)      ⓓ 지도 조회 t(MAP[k] as never)
  //   ⇒ ⭐ ⓒⓓ 는 ★ «닫힌 집합»을 소스에서 읽어 폈다(TaskPriority · TaskStatus · DUE_FILTER_LABEL).
  //   ⇒ ⛔⛔ 그리고 ★ ⓒ 의 `as never` 는 ⇒ ⭐ 열쇠 «타입 검사를 끈다» —
  //     ⇒ ⇒ 없는 열쇠도 tsc 를 통과하고 ⇒ ★ 화면에 ⭐ «열쇠 글자»가 그려진다.
  //     ⇒ ⇒ ⇒ ⭐ 그래서 가드가 ★ 「조립이 만드는 열쇠가 다 선언됐나」를 ⭐ 펴서 잠근다.
  'project.group.manage': { ko: '그룹 관리', en: 'Manage groups', ja: 'グループ管理' },
  'project.mytask.add_group': { ko: '그룹 추가', en: 'Add group', ja: 'グループ追加' },
  'project.mytask.ap_inbox': { ko: '내가 승인할 업무 {n}건', en: '{n} awaiting your approval', ja: '承認待ち {n}件' },
  'project.mytask.ap_pending_aria': { ko: "'{name}' 승인 대기 중", en: "'{name}' is awaiting approval", ja: "'{name}' は承認待ち" },
  'project.mytask.ap_requested_by': { ko: '{name} 요청', en: 'from {name}', ja: '{name} 申請' },
  'project.mytask.chip_all': { ko: '전체', en: 'All', ja: '全体' },
  'project.mytask.chip_progress': { ko: '진행중', en: 'In progress', ja: '進行中' },
  'project.mytask.clear_filters': { ko: '필터 · 검색 해제', en: 'Clear filters', ja: 'フィルターを解除' },
  'project.mytask.col_due': { ko: '기한', en: 'Due', ja: '期限' },
  'project.mytask.col_priority': { ko: '우선순위', en: 'Priority', ja: '優先度' },
  'project.mytask.col_project': { ko: '프로젝트', en: 'Project', ja: 'プロジェクト' },
  'project.mytask.col_tags': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'project.mytask.col_title': { ko: '업무 제목', en: 'Task', ja: 'タスク名' },
  'project.mytask.create_err_title': { ko: '업무 제목을 입력하세요.', en: 'Please enter a task title.', ja: 'タスク名を入力してください。' },
  'project.mytask.create_label_due': { ko: '마감일', en: 'Due date', ja: '期限' },
  'project.mytask.create_label_group': { ko: '그룹', en: 'Group', ja: 'グループ' },
  'project.mytask.create_label_priority': { ko: '우선순위', en: 'Priority', ja: '優先度' },
  'project.mytask.create_label_project': { ko: '프로젝트', en: 'Project', ja: 'プロジェクト' },
  'project.mytask.create_label_title': { ko: '업무 제목', en: 'Task title', ja: 'タスク名' },
  'project.mytask.create_personal': { ko: '개인 (내 할일)', en: 'Personal (My tasks)', ja: '個人（マイタスク）' },
  'project.mytask.create_ph_title': { ko: '예: 주간 보고서 정리', en: 'e.g. Write the weekly report', ja: '例: 週報の作成' },
  'project.mytask.create_submit': { ko: '업무 만들기', en: 'Create task', ja: 'タスクを作成' },
  'project.mytask.create_title': { ko: '업무 생성', en: 'New task', ja: 'タスク作成' },
  'project.mytask.due_apply': { ko: '적용', en: 'Apply', ja: '適用' },
  'project.mytask.due_clear': { ko: '기한 없음', en: 'No due date', ja: '期限なし' },
  'project.mytask.due_pick': { ko: '기한 정하기', en: 'Set due date', ja: '期限を決める' },
  'project.mytask.due_pick_aria': { ko: "'{name}' 기한 정하기", en: "Set due date for '{name}'", ja: "'{name}' の期限を決める" },
  'project.mytask.due_pick_date': { ko: '날짜 직접 고르기', en: 'Pick a date', ja: '日付を選ぶ' },
  'project.mytask.due_q_next_week': { ko: '일주일 뒤', en: 'In a week', ja: '1週間後' },
  'project.mytask.due_q_plus_week': { ko: '기한 +7일', en: 'Due +7 days', ja: '期限 +7日' },
  'project.mytask.due_q_today': { ko: '오늘', en: 'Today', ja: '今日' },
  'project.mytask.due_q_tomorrow': { ko: '내일', en: 'Tomorrow', ja: '明日' },
  'project.mytask.empty': { ko: '표시할 업무가 없습니다.', en: 'No tasks to show.', ja: '表示するタスクがありません。' },
  'project.mytask.empty_filtered': { ko: '조건에 맞는 업무가 없습니다.', en: 'No tasks match these filters.', ja: '条件に一致するタスクがありません。' },
  'project.mytask.empty_filtered_desc': {
    ko: '검색어나 필터를 바꾸거나, 아래에서 조건을 모두 해제해 보세요.',
    en: 'Try a different search or filter, or clear them all below.',
    ja: '検索語やフィルターを変えるか、下ですべて解除してください。',
  },
  'project.mytask.err_add': { ko: '할일 추가 실패', en: 'Failed to add task', ja: 'タスク追加失敗' },
  'project.mytask.err_more': { ko: '다음 목록을 불러오지 못했습니다.', en: 'Could not load more tasks.', ja: '続きを読み込めませんでした。' },
  'project.mytask.filter_aria': { ko: '상태 필터', en: 'Status filter', ja: '状態フィルター' },
  'project.mytask.filtered_count': { ko: '{n}건', en: '{n} tasks', ja: '{n}件' },
  'project.mytask.group_all': { ko: '그룹 · 전체', en: 'Group · all', ja: 'グループ · 全体' },
  'project.mytask.group_filter_aria': { ko: '그룹으로 좁혀 보기', en: 'Filter by group', ja: 'グループで絞り込み' },
  'project.mytask.heading': { ko: '내 업무', en: 'My tasks', ja: 'マイタスク' },
  'project.mytask.hide_done': { ko: '완료 {n}건 접기', en: 'Hide {n} completed', ja: '完了 {n}件を隠す' },
  'project.mytask.load_more': { ko: '더 보기', en: 'Load more', ja: 'さらに表示' },
  'project.mytask.loading_aria': { ko: '내 업무 불러오는 중', en: 'Loading my tasks', ja: 'マイタスクを読み込み中' },
  'project.mytask.loading_more': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'project.mytask.more_menu': { ko: '그룹·템플릿 관리 메뉴', en: 'Group and template menu', ja: 'グループ・テンプレートメニュー' },
  'project.mytask.new': { ko: '업무 생성', en: 'New task', ja: 'タスク作成' },
  'project.mytask.pri_high': { ko: '높음', en: 'High', ja: '高' },
  'project.mytask.pri_low': { ko: '낮음', en: 'Low', ja: '低' },
  'project.mytask.pri_medium': { ko: '보통', en: 'Medium', ja: '中' },
  'project.mytask.progress_aria': { ko: '진행률 {n}퍼센트', en: '{n} percent complete', ja: '進捗 {n}パーセント' },
  'project.mytask.search_aria': { ko: '업무 검색', en: 'Search tasks', ja: 'タスク検索' },
  'project.mytask.search_clear': { ko: '검색어 지우기', en: 'Clear search', ja: '検索語を消去' },
  'project.mytask.search_ph': { ko: '업무 · 프로젝트 이름으로 찾기', en: 'Search task or project', ja: 'タスク・プロジェクト名で検索' },
  'project.mytask.show_done': { ko: '완료 {n}건 보기', en: 'Show {n} completed', ja: '完了 {n}件を表示' },
  'project.mytask.shown_of_total': {
    ko: '전체 {total}건 중 {shown}건 표시',
    en: 'Showing {shown} of {total}',
    ja: '全{total}件中{shown}件を表示',
  },
  'project.mytask.sort_aria': { ko: '정렬 기준', en: 'Sort by', ja: '並び順' },
  'project.mytask.sort_due_short': { ko: '마감임박순', en: 'Due soon', ja: '期限が近い順' },
  'project.mytask.sort_priority_short': { ko: '우선순위순', en: 'Priority', ja: '優先度順' },
  'project.mytask.sort_recent_short': { ko: '최근 등록순', en: 'Newest', ja: '新着順' },
  'project.mytask.toggle_aria': { ko: "'{name}' 완료 처리", en: "Complete '{name}'", ja: "'{name}'を完了" },
  'project.mytask.undo_btn': { ko: '되돌리기', en: 'Undo', ja: '元に戻す' },
  'project.mytask.undo_close': { ko: '알림 닫기', en: 'Dismiss', ja: '通知を閉じる' },
  'project.mytask.undo_marked_done': { ko: "'{name}' 을 완료 처리했습니다.", en: "Marked '{name}' as done.", ja: "'{name}' を完了にしました。" },
  'project.mytask.undo_reopened': { ko: "'{name}' 을 다시 할 일로 되돌렸습니다.", en: "Reopened '{name}'.", ja: "'{name}' を未着手に戻しました。" },
  'project.mytasks.filter_clear': { ko: '필터 해제', en: 'Clear filter', ja: 'フィルター解除' },
  'project.mytasks.filter_due_soon': { ko: '마감임박', en: 'Due soon', ja: '期限間近' },
  'project.mytasks.filter_overdue': { ko: '지연', en: 'Overdue', ja: '遅延' },
  'project.mytasks.filter_risk': { ko: '위험', en: 'At risk', ja: 'リスク' },

  /* ════════════════════════════════════════════════════════════════════
   * ★★★ 정본 이관 — ⭐ `feature/dev-syk` 의 프로젝트 메뉴 열쇠 (2026-10-10)
   *
   * ## ⭐ 사용자 지시 (오늘 · 글자 그대로)
   *
   * 「feature/dev-syk 의 프로젝트 메뉴가 정본이다. 프로젝트메뉴만 동일하게 이관시켜달라.
   *   그냥 그대로 이관해달라. 추후 잘못된 설계나 기능은 해당 화면을 보면서 사용자가 변경예정이다.」
   *
   * ## ⚠ 그래서 ★ 값을 ⭐ «고치지 않았다»
   *
   * 아래 100개는 ★ `origin/feature/dev-syk` ⭐ `dd05a6fcd` 의 이 파일에서 ★ **글자 그대로** 옮긴 것이다.
   * ⇒ ⛔ 우리가 쓰던 열쇠·값은 ★ 하나도 ⭐ 고치거나 지우지 않았다(additive).
   * ⇒ ★ 선언만 더했다 — ⭐ 부르는 쪽(`pages/ProjectPage/`)은 ★ 디자인 세션이 짓는다.
   *
   * ## ⛔⛔ 정본에 있는데 ★ «일부러 안 가져온» 것 ⇒ ⭐ 둘
   *
   *     ⇒ project[.]detail[.]back       「대시보드」
   *     ⇒ project[.]detail[.]back_aria  「프로젝트 대시보드로 돌아가기」
   * ⇒ ★ 까닭 ⇒ ⭐ 이 파일이 **이미 적고 있다**(위 「v2 드릴다운」 절):
   *   「⛔ 2026-10-07 ★ 사용자 결정 ⓑ — … 를 ⭐ 지웠다 … ⇒ ⛔ 「대시보드」를 ★ 되살리지 마십시오.
   *    그 글자는 ⭐ 2026-07-25 이후 ★ 아무 화면의 이름도 아니다」
   * ⇒ ⇒ ★ 즉 ⇒ ⭐ 정본이 ★ 그 결정보다 ⭐ **앞서 있다** ⇒ ⇒ 가져오면 ★ 결정을 ⭐ 되돌린다.
   *
   * ## ⚠ 그리고 ★ 「값이 다른」 열쇠 ⭐ 셋 ⇒ ⛔ **덮지 않았다**
   *
   *     ⇒ project[.]err_fetch         우리 「프로젝트 홈 …」  ⇔ 정본 「프로젝트 대시보드 …」
   *     ⇒ project[.]home[.]overdue      우리 D+{n}            ⇔ 정본 「기한 초과」
   *     ⇒ project[.]home[.]overdue_n    우리 D+{n}            ⇔ 정본 「지연 {n}」
   * ⇒ ★ 셋 다 ⇒ ⭐ **우리 쪽이 더 나중의 사용자 결정**이다 —
   *   ⇒ 「대시보드」는 위 ⓑ 가 걷었고, `D+{n}` 은 ★ 2026-10-08 사용자 결정(「네 수정」·ⓨ)이다
   *     (⇒ 커밋 `d834f6718` · `42e1ee081` · 그 까닭은 위 `overdue` 절에 적혀 있다).
   * ⇒ ⇒ ⚠ 그러니 ★ 「정본이니 맞춘다」로 ⭐ 되돌리지 말 것.
   *
   * ## ✅ 「이모지 금지」는 ★ 여기서 ⭐ 물음이 안 된다
   *
   * 쟀다 — ★ 정본에만 있는 102 에 ⇒ ⭐ **그림문자(🗑 📅 …)가 0** 이다.
   * ⇒ ★ 화살표(`→`)가 든 값이 ⭐ 둘인데 ⇒ ⚠ 그것은 ★ 이 레포가 ⭐ 이미 쓰는 꼴이다:
   *   우리 i18n 48파일의 값 줄에 ★ 그림문자 ⭐ 1 · 화살표 ⭐ 18 · 그밖기호 ⭐ 25,
   *   ⇒ ⇒ **이 파일 자체**에도 ★ 화살표 3 · 그밖기호 6 이 ⭐ 있다. ⇒ ⇒ ✅ 그래서 넣었다.
   *
   * ## ⛔⛔ 이 주석이 ★ «열쇠를 홑따옴표로» 적지 ⭐ 않는 까닭
   *
   * 레인을 가른 세션이 ★ 「정본에만 몇 개 남았나」를 ⇒ ⭐ 이렇게 잰다:
   *   git grep -ohIE "'project[.][a-z0-9_.]+'" <ref> -- apps/frontend/src
   * ⇒ ⇒ ⛔ 그 거르개는 ★ 주석 속 글자도 ⭐ «센다». ⇒ ★ 그래서 ⭐ 안 가져온 둘을
   *   이 주석에 ★ 홑따옴표로 적으면 ⇒ ⇒ ⭐ 「정본에만 ★ 0」이 나와 ⇒ ⛔ 「이관 끝」으로 읽힌다
   *   (⇒ 실제로 ★ 한 번 그렇게 나왔다 ⇒ ⭐ 그래서 `project[.]detail[.]back` 꼴로 적는다).
   * ⇒ ⚠ 뒷사람도 ★ 이 꼴을 ⭐ 지켜 주기 바란다.
   *
   * ## ⚠ 재는 명령 (⇒ 수를 믿을 근거)
   *
   * ```
   * # 선언을 ★ «덩이째» 맞댄다 — ⇒ ⛔ 열쇠 이름만 세면 ⭐ 「값이 다른 것」을 못 본다
   * git show origin/feature/dev-syk:apps/frontend/src/lib/project-i18n.ts | grep -cE "^  '"  # 606
   * git show origin/main:…                                                | grep -cE "^  '"  # 510
   * # ⇒ 공유 504 · 정본에만 102 · 우리에만 6 · 값이 다른 것 3
   * ```
   * ⚠ 열쇠 «이름»만 훑으면 ⇒ ⛔ 대문자가 든 아홉(`project.status.TODO` 등)을 ★ 놓친다
   *   — 그 아홉은 ⭐ 양쪽에 다 있어 ★ 이 판에서는 구멍이 아니었다.
   * ════════════════════════════════════════════════════════════════════ */

  /* ── project.mytask.* ⇒ 36개 ── */
  'project.mytask.chip_done': { ko: '완료', en: 'Done', ja: '完了' },
  'project.mytask.chip_risk': { ko: '위험', en: 'At risk', ja: 'リスク' },
  'project.mytask.sort_due': { ko: '정렬 · 마감임박순', en: 'Sort · due soon', ja: '並び順 · 期限が近い順' },
  'project.mytask.sort_priority': { ko: '정렬 · 우선순위순', en: 'Sort · priority', ja: '並び順 · 優先度' },
  'project.mytask.sort_recent': { ko: '정렬 · 최근 등록순', en: 'Sort · newest', ja: '並び順 · 新着' },
  'project.mytask.matrix_title': { ko: '지금 뭐부터? · 중요 × 급함', en: 'What first? · Important × Urgent', ja: '今どれから? · 重要 × 緊急' },
  'project.mytask.matrix_now': { ko: '지금 당장', en: 'Do now', ja: '今すぐ' },
  'project.mytask.matrix_now_hint': { ko: '중요한데 마감이 코앞 — 여기부터', en: 'Important and due within 2 days', ja: '重要で締切が目前 — ここから' },
  'project.mytask.matrix_plan': { ko: '계획해서', en: 'Schedule', ja: '計画して' },
  'project.mytask.matrix_plan_hint': { ko: '중요하지만 아직 시간 있음 — 미루면 위 칸으로 온다', en: 'Important, not urgent yet — it moves up if you wait', ja: '重要だがまだ余裕 — 放置すると上の枠へ' },
  'project.mytask.matrix_quick': { ko: '빨리 쳐내기', en: 'Knock out', ja: 'サッと片付け' },
  'project.mytask.matrix_quick_hint': { ko: '급하지만 중요치는 않음 — 하루를 여기 쓰지 말 것', en: 'Urgent but not important — don’t spend the day here', ja: '急ぎだが重要ではない — ここに一日使わない' },
  'project.mytask.matrix_later': { ko: '여유', en: 'Later', ja: '余裕' },
  'project.mytask.matrix_later_hint': { ko: '급하지도 중요치도 않음', en: 'Neither urgent nor important', ja: '急ぎでも重要でもない' },
  'project.mytask.matrix_axis_important': { ko: '중요', en: 'Important', ja: '重要' },
  'project.mytask.matrix_axis_urgent': { ko: '급함', en: 'Urgent', ja: '緊急' },
  'project.mytask.matrix_cell_aria': { ko: '{label} {n}건', en: '{label}, {n} tasks', ja: '{label} {n}件' },
  'project.mytask.matrix_legend_dot': { ko: '점 하나 = 업무 하나 (누르면 열림)', en: 'One dot = one task (click to open)', ja: '点1つ = タスク1件（クリックで開く）' },
  'project.mytask.matrix_legend_over': { ko: '지연', en: 'overdue', ja: '遅延' },
  'project.mytask.nextup_rank': { ko: '{n}순위', en: 'Priority {n}', ja: '優先度{n}' },
  'project.mytask.nextup_r1_hint': { ko: '중요한데 마감이 코앞 — 여기부터', en: 'Important and due within 2 days — start here', ja: '重要で締切が目前 — ここから' },
  'project.mytask.nextup_r2_hint': { ko: '중요하지만 아직 시간 있음 — 미루면 1순위로 올라온다', en: 'Important, not urgent yet — it becomes priority 1 if you wait', ja: '重要だがまだ余裕 — 放置すると優先度1へ' },
  'project.mytask.nextup_r3_hint': { ko: '급하지만 중요치는 않음 — 하루를 여기 쓰지 말 것', en: 'Urgent but not important — don’t spend the day here', ja: '急ぎだが重要ではない — ここに一日使わない' },
  'project.mytask.nextup_r4_hint': { ko: '급하지도 중요치도 않음', en: 'Neither urgent nor important', ja: '急ぎでも重要でもない' },
  'project.mytask.nextup_none': { ko: '없음', en: 'None', ja: 'なし' },
  'project.mytask.nextup_rank_aria': { ko: '{rank} {label} {n}건', en: '{rank} {label}, {n} tasks', ja: '{rank} {label} {n}件' },
  'project.mytask.col_detail': { ko: '상세', en: 'Detail', ja: '詳細' },
  'project.mytask.quick_ph': { ko: '할일을 입력하고 Enter (내가 직접 추가)', en: 'Type a task and press Enter', ja: 'タスクを入力してEnter' },
  'project.mytask.confirm_hint': {
    ko: '이제 하지 않기로 한 항목은 옆의 휴지통으로 지우면 됩니다.',
    en: 'Delete any item you no longer need with the trash icon.',
    ja: 'やらないことにした項目はゴミ箱で削除してください。',
  },
  'project.mytask.confirm_check_all': { ko: '남은 항목 체크하고 완료', en: 'Check all and finish', ja: '残りをチェックして完了' },
  'project.mytask.confirm_cancel': { ko: '돌아가기', en: 'Back', ja: '戻る' },
  'project.mytask.left_hint': { ko: '남은 항목 {n}개', en: '{n} left', ja: '残り{n}件' },
  'project.mytask.ap_cancel': { ko: '요청 취소', en: 'Withdraw request', ja: '申請を取り消す' },
  'project.mytask.ap_approved_at': { ko: '{date} 승인됨', en: 'Approved {date}', ja: '{date} 承認' },
  'project.mytask.ap_inbox_empty': { ko: '승인할 업무가 없습니다.', en: 'Nothing awaiting your approval.', ja: '承認する業務はありません。' },
  'project.mytask.nextup_legend': { ko: '중요 → 급함(2일 내 마감) 순. 줄을 누르면 그 업무가 열립니다.', en: 'Ranked by important first, then urgent (due within 2 days). Click a row to open it.', ja: '重要度 → 緊急度（2日以内）の順。行をクリックすると開きます。' },

  /* ── project.home.* ⇒ 24개 ── */
  'project.home.aw001.space_delete_aria': { ko: '{name} 스페이스 삭제', en: 'Delete space {name}', ja: 'スペース {name} を削除' },
  'project.home.aw001.project_open_aria': { ko: '{name} 프로젝트 열기', en: 'Open project {name}', ja: 'プロジェクト {name} を開く' },
  'project.home.aw001.project_delete_aria': { ko: '{name} 프로젝트 삭제', en: 'Delete project {name}', ja: 'プロジェクト {name} を削除' },
  'project.home.aw001.project_meta': {
    ko: '{space} · 태스크 {done}/{total}',
    en: '{space} · {done}/{total} tasks',
    ja: '{space} · タスク {done}/{total}',
  },
  'project.home.aw001.projects_empty': {
    ko: '진행 중인 프로젝트가 없어요. 새 프로젝트를 만들어 시작하세요.',
    en: 'No active projects yet. Create one to get started.',
    ja: '進行中のプロジェクトがありません。新規作成して始めましょう。',
  },
  'project.home.aw005.search_shortcut': { ko: '단축키 Ctrl+K', en: 'Ctrl+K', ja: 'ショートカット Ctrl+K' },
  'project.home.aw005.search_clear': { ko: '검색어 지우기', en: 'Clear search', ja: '検索語を消去' },
  'project.home.aw005.search_searching': { ko: '찾는 중…', en: 'Searching…', ja: '検索中…' },
  'project.home.aw005.search_results_aria': { ko: '통합 검색 결과', en: 'Search results', ja: '統合検索結果' },
  'project.home.aw005.group_spaces': { ko: '스페이스', en: 'Spaces', ja: 'スペース' },
  'project.home.aw005.group_projects': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'project.home.aw005.group_tasks': { ko: '업무', en: 'Tasks', ja: 'タスク' },
  'project.home.aw005.search_none': { ko: '‘{q}’ 와(과) 일치하는 결과가 없어요.', en: 'Nothing matches ‘{q}’.', ja: '‘{q}’ に一致する結果がありません。' },
  'project.home.aw005.search_more': { ko: '더 정확히 입력하면 결과가 좁혀져요', en: 'Refine your search to narrow results', ja: '入力を絞ると結果が絞り込まれます' },
  'project.home.aw005.no_space': { ko: '스페이스 없음', en: 'No space', ja: 'スペースなし' },
  'project.home.aw005.fav_heading': { ko: '즐겨찾기', en: 'Favorites', ja: 'お気に入り' },
  'project.home.aw005.sort_overdue': { ko: '지연 많은 순', en: 'Most overdue', ja: '遅延が多い順' },
  'project.home.aw005.sort_progress': { ko: '진행률 낮은 순', en: 'Least progress', ja: '進捗が低い順' },
  'project.home.aw005.sort_label': { ko: '정렬', en: 'Sort', ja: '並び順' },
  'project.home.aw005.stat_open_due_soon': { ko: '마감임박 업무 보기', en: 'View tasks due soon', ja: '期限間近のタスクを見る' },
  'project.home.aw005.stat_open_overdue': { ko: '지연 업무 보기', en: 'View overdue tasks', ja: '遅延タスクを見る' },
  'project.home.aw005.stat_open_risk': { ko: '위험 업무 보기', en: 'View at-risk tasks', ja: 'リスクタスクを見る' },
  'project.home.aw005.loading': { ko: '불러오는 중', en: 'Loading', ja: '読み込み中' },
  'project.home.aw005.members_more': { ko: '외 {n}명', en: '+{n} more', ja: '他 {n}人' },

  /* ── project.space.* ⇒ 13개 ── */
  'project.space.delete.confirm': { ko: "'{name}' 스페이스를 삭제할까요?", en: "Delete space '{name}'?", ja: "スペース'{name}'を削除しますか？" },
  'project.space.delete.cascade_warn': {
    ko: '이 스페이스의 프로젝트 {n}개도 함께 삭제됩니다 — {names}',
    en: 'Its {n} project(s) will be deleted too — {names}',
    ja: 'このスペースのプロジェクト{n}件も一緒に削除されます — {names}',
  },
  'project.space.delete.projects_none': { ko: '이 스페이스에는 프로젝트가 없습니다.', en: 'This space has no projects.', ja: 'このスペースにプロジェクトはありません。' },
  'project.space.delete.irreversible': { ko: '삭제하면 되돌릴 수 없습니다.', en: 'This cannot be undone.', ja: '削除すると元に戻せません。' },
  'project.space.delete.err_forbidden': { ko: '스페이스 삭제 권한이 없습니다 (리더만 가능).', en: 'No permission to delete this space (leader only).', ja: 'スペース削除の権限がありません（リーダーのみ）。' },
  'project.space.delete.err': { ko: '스페이스 삭제 실패', en: 'Failed to delete space', ja: 'スペース削除失敗' },
  'project.space.create_title': { ko: '스페이스 생성', en: 'Create space', ja: 'スペース作成' },
  'project.space.color_and_name': { ko: '색상 및 스페이스명', en: 'Color and name', ja: '色とスペース名' },
  'project.space.name_ph': { ko: '스페이스명을 입력하세요', en: 'Enter a space name', ja: 'スペース名を入力' },
  'project.space.about_title': { ko: '스페이스란?', en: 'What is a space?', ja: 'スペースとは？' },
  'project.space.about_1': {
    ko: '협업에 필요한 프로젝트를 생성할 수 있는 공간입니다.',
    en: 'A place to create the projects your space works on together.',
    ja: '協業に必要なプロジェクトを作成できる空間です。',
  },
  'project.space.about_2': {
    ko: '만든 사람이 마스터가 되고, 멤버는 언제든 초대할 수 있습니다.',
    en: 'You become its owner, and can invite members at any time.',
    ja: '作成者がマスターになり、メンバーはいつでも招待できます。',
  },
  'project.space.err_name': { ko: '스페이스명을 입력하세요.', en: 'Please enter a space name.', ja: 'スペース名を入力してください。' },

  /* ── project.info.* ⇒ 13개 ── */
  'project.info.open': { ko: '프로젝트 정보', en: 'Project info', ja: 'プロジェクト情報' },
  'project.info.tab_overview': { ko: '개요', en: 'Overview', ja: '概要' },
  'project.info.tab_members': { ko: '멤버', en: 'Members', ja: 'メンバー' },
  'project.info.tab_activity': { ko: '활동 내역', en: 'Activity', ja: '活動履歴' },
  'project.info.no_desc': { ko: '설명이 없습니다.', en: 'No description.', ja: '説明がありません。' },
  'project.info.due': { ko: '마감일', en: 'Due date', ja: '締切日' },
  'project.info.team': { ko: '소속', en: 'Space', ja: '所属' },
  'project.info.progress': { ko: '진행률', en: 'Progress', ja: '進捗' },
  'project.info.counts': { ko: '완료 {done} / 전체 {total}', en: '{done} done / {total} total', ja: '完了 {done} / 全体 {total}' },
  'project.info.act_task_created': { ko: '{name}님이 업무를 생성했습니다.', en: '{name} created a task.', ja: '{name}さんがタスクを作成しました。' },
  'project.info.act_project_created': { ko: '{name}님이 프로젝트를 생성했습니다.', en: '{name} created the project.', ja: '{name}さんがプロジェクトを作成しました。' },
  'project.info.act_empty': { ko: '아직 활동이 없습니다.', en: 'No activity yet.', ja: 'まだ活動がありません。' },
  'project.info.act_hint': {
    ko: '업무를 누르면 상세가 열립니다',
    en: 'Click a task to open its detail',
    ja: 'タスクを押すと詳細が開きます',
  },

  /* ── project.team.* ⇒ 11개 ── */
  'project.team.aw.subtitle': {
    ko: '이름만 정하면 바로 만들어져요. 멤버는 지금 추가해도, 나중에 해도 됩니다.',
    en: 'A name is all it takes. Add members now or later.',
    ja: '名前だけで作成できます。メンバーは今でも後でも追加できます。',
  },
  'project.team.aw.invite_label': { ko: '멤버 초대 (선택)', en: 'Invite members (optional)', ja: 'メンバー招待（任意）' },
  'project.team.aw.submit': { ko: '스페이스 만들기', en: 'Create space', ja: 'スペース作成' },
  'project.team.aw.submit_n': { ko: '스페이스 만들기 ({n}명 초대)', en: 'Create space (invite {n})', ja: 'スペース作成（{n}名招待）' },
  'project.team.aw.empty_search': { ko: '검색 결과가 없습니다.', en: 'No results.', ja: '検索結果がありません。' },
  'project.team.aw.add_aria': { ko: "'{name}' 선택", en: "Select '{name}'", ja: "'{name}'を選択" },
  'project.team.aw.err_invite_n': {
    ko: '스페이스는 만들어졌지만 {names} 초대에 실패했습니다. 스페이스 화면에서 다시 초대해 주세요.',
    en: 'Space created, but inviting {names} failed. Try again from the space page.',
    ja: 'スペースは作成されましたが、{names} の招待に失敗しました。スペース画面から再試行してください。',
  },
  'project.team.aw.err_invite': { ko: '스페이스는 만들어졌지만 일부 멤버 초대에 실패했습니다.', en: 'Space created, but some invites failed.', ja: 'スペースは作成されましたが、一部の招待に失敗しました。' },
  'project.team.aw.desc_label': { ko: '설명 (선택)', en: 'Description (optional)', ja: '説明（任意）' },
  'project.team.aw.desc_ph': {
    ko: '이 스페이스가 무엇을 하는 곳인지 적어 두세요',
    en: 'What is this space for?',
    ja: 'このスペースの用途を書いておきましょう',
  },
  'project.team.aw.later': { ko: '나중에 초대하기 →', en: 'Invite later →', ja: '後で招待する →' },

  /* ── project.settings.* ⇒ 2개 ── */
  'project.settings.require_approval': { ko: '완료 승인 받기', en: 'Require approval to finish', ja: '完了に承認を必要とする' },
  'project.settings.require_approval_hint': {
    ko: '켜면 담당자가 [완료 요청] 을 하고, 관리자(MANAGER 이상)가 승인해야 완료됩니다.',
    en: 'When on, the assignee requests approval and a project manager approves it.',
    ja: 'オンにすると担当者が申請し、管理者（MANAGER以上）の承認で完了します。',
  },

  /* ── project.mytasks.* ⇒ 1개 ── */
  'project.mytasks.filter_empty': { ko: '해당하는 업무가 없어요.', en: 'No tasks match this filter.', ja: '該当するタスクがありません。' },
} satisfies Record<string, Record<Lang, string>>;

export type ProjectI18nKey = keyof typeof MESSAGES;

/** fallback chain (lang → en → ko) + {param} 치환 — task-i18n 동형. */
export function tProject(key: ProjectI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getProjectLang(): Lang { return getSharedLang(); }
export function setProjectLang(value: Lang): void { setSharedLang(value); }
export function subscribeProjectLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}

// 테스트 전수 가드용 export (i18n 누락 키 런타임 은닉 방어 — antipattern 학습 반영)
export const PROJECT_I18N_MESSAGES = MESSAGES;
